/**
 * Report Viewer - Display report with citations, evidence tracing, and collaboration
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { ScrollableTabStrip } from '../UI/ScrollableTabStrip';
import { ReportContentTab, type ReportContentTabRef } from './ReportContentTab';
import { getReport, downloadReportV1, downloadReportV2, downloadReportPDF, downloadReportDOCX, downloadReportXLSX, trackReportView, trackReportDownload, removeCitation, regenerateSections, type Report } from '../../api/reports';
import { listArtifacts, downloadArtifactWithToast, getArtifact } from '../../api/artifacts';
import { ReportAnalytics } from './ReportAnalytics';
import { ReportSharing } from './ReportSharing';
import { ReportCollaboration } from './ReportCollaboration';
import { ReportActions } from './ReportActions';
import { AddCitationModal } from './AddCitationModal';
import { CustomSectionsTab } from './CustomSectionsTab';
import { QAChecklistPanel, QAFormattingPanel, QAGrammarPanel, QAPreSubmissionPanel, QAScoreReadinessPanel, FeedbackPlanPreviewModal } from '../QA';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfig, useFeatureFlag } from '../../contexts/ConfigContext';
import { ReviewDashboard } from '../Review';
import { StaffAssignmentsTab } from './StaffAssignmentsTab';
import { useToastStore } from '../../stores/toast';
import {
  FileText,
  Download,
  Edit,
  Share2,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  BookOpen,
  ExternalLink,
  GitBranch,
  History,
  TrendingUp,
  Shield,
  Code,
  FileCode,
  Save,
  X as XIcon,
  Trash2,
  RefreshCw,
  HelpCircle,
  Plus,
  Scissors,
  ClipboardCheck,
  Users,
  AlertTriangle,
  Loader2,
  ChevronUp,
} from 'lucide-react';

interface ReportViewerProps {
  reportId: string;
  onEdit?: () => void;
  onClose?: () => void;
  /** When set (RFP proposal viewer), gates mutations by proposals.edit / proposals.write / etc. */
  proposalWriteAccess?: {
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

interface ReportVersion {
  version: number;
  created_at: string;
  created_by: string;
  changes: string;
  status: string;
}

/** KBF-035: Template response for report template endpoint (full requirements). */
interface ReportTemplateResponse {
  sections?: { name: string; description?: string }[];
  default_questions?: { section: string; question: string }[];
  template_source?: string;
}

/** KBF-035: Replace truncated requirement lines (ending with "...") under ## SectionName with full text from template. */
function preprocessMarkdownWithFullRequirements(
  markdown: string,
  template: ReportTemplateResponse | undefined
): string {
  if (!markdown || !template?.sections?.length) return markdown;
  const sectionToFullText = new Map<string, string>();
  for (const s of template.sections || []) {
    if (s.name && s.description) sectionToFullText.set(s.name.trim(), s.description);
  }
  for (const q of template.default_questions || []) {
    if (q.section && q.question) sectionToFullText.set(q.section.trim(), q.question);
  }
  if (sectionToFullText.size === 0) return markdown;

  const lines = markdown.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const h2Match = line.match(/^##\s+(.+)$/);
    if (h2Match) {
      out.push(line);
      i += 1;
      const sectionName = h2Match[1].trim();
      const fullText = sectionToFullText.get(sectionName);
      // Peek at next non-empty line: if it looks like truncated requirement (ends with "..."), replace with full
      while (i < lines.length && lines[i].trim() === '') {
        out.push(lines[i]);
        i += 1;
      }
      if (i < lines.length && fullText) {
        const next = lines[i];
        const trimmed = next.trim();
        const looksTruncated =
          (trimmed.endsWith('...') || trimmed.endsWith('…') || /\.{2,}\s*$/.test(trimmed)) &&
          (trimmed.startsWith('*') || !trimmed.startsWith('#'));
        if (looksTruncated && trimmed.length < fullText.length) {
          out.push(`*${fullText}*`);
          i += 1;
        } else {
          out.push(lines[i]);
          i += 1;
        }
      }
      continue;
    }
    out.push(line);
    i += 1;
  }
  return out.join('\n');
}

/** Parse v1 markdown to get unique recommendation count (for legacy reports where API still has total question count). */
function getParsedEvidenceGapsCount(v1Content: string): number | null {
  if (!v1Content || !v1Content.trim()) return null;
  const sectionMatch = v1Content.match(/## Evidence Gaps & Recommendations\s*([\s\S]*?)(?=\n##\s|$)/i);
  const section = sectionMatch ? sectionMatch[1] : '';
  if (!section.trim()) return null;
  const questionFromBlock = (block: string): string => {
    const lines = block.trim().split('\n');
    let end = lines.length;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].match(/^[-*]\s*\*\*/)) { end = i; break; }
    }
    return lines.slice(0, end).join('\n').trim();
  };
  const untilMediumOrRule = '(?=\\n---|###\\s*(?:🟡\\s*)?Medium:\\s*Low Confidence|###\\s*(?:🟢\\s*)?Low Priority:\\s*Incomplete|$)';
  const untilLowPriorityOrRule = '(?=\\n---|###\\s*(?:🟢\\s*)?Low Priority:\\s*Incomplete|$)';
  const needKeys = new Set<string>();
  const criticalBlock = section.match(new RegExp('###\\s*(?:🔴\\s*)?Critical:\\s*No Evidence Found\\s*([\\s\\S]*?)' + untilMediumOrRule, 'i'))?.[1] || '';
  criticalBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
    const needMatch = block.match(/[-*]\s*\*\*Need:\*\*\s*([^\n]+)/);
    const question = questionFromBlock(block);
    if (question && needMatch) needKeys.add(needMatch[1].trim().slice(0, 120));
  });
  const recKeys = new Set<string>();
  const mediumBlock = section.match(new RegExp('###\\s*(?:🟡\\s*)?Medium:\\s*Low Confidence Answers\\s*([\\s\\S]*?)' + untilLowPriorityOrRule, 'i'))?.[1] || '';
  mediumBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
    const recMatch = block.match(/[-*]\s*\*\*Recommendation:\*\*\s*([^\n]+)/);
    const question = questionFromBlock(block);
    if (question && recMatch) recKeys.add(recMatch[1].trim().slice(0, 120));
  });
  const enhKeys = new Set<string>();
  const incompleteBlock = section.match(/###\s*(?:🟢\s*)?Low Priority:\s*Incomplete Coverage\s*([\s\S]*?)(?=\n---|$)/i)?.[1] || '';
  incompleteBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
    const enhMatch = block.match(/[-*]\s*\*\*Enhancement:\*\*\s*([^\n]+)/);
    const question = questionFromBlock(block);
    if (question && enhMatch) enhKeys.add(enhMatch[1].trim().slice(0, 80));
  });
  if (needKeys.size === 0 && recKeys.size === 0 && enhKeys.size === 0) return null;
  return needKeys.size + recKeys.size + enhKeys.size;
}

/** Normalize confidence to 0–100 for display. Handles 0–1 decimal, 0–100 percentage, or wrong scale (e.g. 28689 or 286.89 → 29%). */
function normalizeConfidenceForDisplay(value: number | undefined | null): number | null {
  if (value === undefined || value === null || typeof value !== 'number' || isNaN(value)) return null;
  let pct: number;
  if (value <= 1) {
    pct = value * 100;
  } else {
    pct = value;
    while (pct > 100) pct /= 10;
  }
  pct = Math.round(Math.min(100, Math.max(0, pct)));
  return pct;
}

/** Truncate text to a max number of words for display; full text remains in title. */
function truncateToWords(text: string, maxWords: number = 6): string {
  if (!text || typeof text !== 'string') return text ?? '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/);
  if (words.length <= maxWords) return trimmed;
  return words.slice(0, maxWords).join(' ') + '…';
}

/** Wraps citation/chunk content: show first 3 lines, then "Show more" / "Show less" so tall chunks don't fill the screen. */
function CollapsibleChunkContent({
  children,
  lines = 3,
}: {
  children: React.ReactNode;
  lines?: number;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const lineHeightRem = 1.5;
  const maxHeightRem = lines * lineHeightRem;
  return (
    <div className="space-y-1">
      <div
        className={expanded ? '' : 'overflow-hidden'}
        style={expanded ? undefined : { maxHeight: `${maxHeightRem}rem` }}
      >
        {children}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus:underline"
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}

// PII Panel placeholder (dynamically loaded from rfp-agent plugin when available)
function PIIPanelSlot({ reportId }: { reportId: string }) {
  const [PIIPanel, setPIIPanel] = React.useState<React.ComponentType<{ reportId: string }> | null>(null);

  React.useEffect(() => {
    const load = async () => {
      try {
        // @ts-ignore - Plugin path may not be available at compile time
        const module = await import('../../../../plugins/rfp-agent/src/index');
        if (module?.PIIAnonymizationPanel) {
          setPIIPanel(() => module.PIIAnonymizationPanel);
          return;
        }
      } catch (e) {
        console.debug('PIIAnonymizationPanel import failed:', e);
      }
      try {
        // @ts-ignore
        const module = await import('../../../../plugins/rfp-agent/src/components/proposal-management/PIIAnonymizationPanel');
        if (module?.PIIAnonymizationPanel) setPIIPanel(() => module.PIIAnonymizationPanel);
      } catch (e) {
        console.debug('PIIAnonymizationPanel direct import failed:', e);
      }
    };
    load();
  }, []);

  if (!PIIPanel) return null;
  return <PIIPanel reportId={reportId} />;
}

// QA Tab Component (available for all reports)
function QATabContent({
  reportId,
  executiveSummaryVersionLabel,
  isProposalReport = false,
}: {
  reportId: string;
  /** Latest executive-summary version marker from report.meta (aligns Score Readiness with Report Content). */
  executiveSummaryVersionLabel?: string;
  /** When false (e.g. workplan), hides Score Readiness panel which is proposal-only. */
  isProposalReport?: boolean;
}) {
  const { hasPermission } = usePermissions();
  const [feedbackPlanOpen, setFeedbackPlanOpen] = React.useState(false);
  const [fbAutoCompute, setFbAutoCompute] = React.useState(true);
  const [fbExpandPrompt, setFbExpandPrompt] = React.useState(false);

  // Check if user is admin - admins get bulk action buttons
  const isAdmin = hasPermission('admin') || hasPermission('admin_tech') || hasPermission('keystone_proposal_admin_tech') || hasPermission('keystone_rfp_admin_tech') || hasPermission('*');

  return (
    <div className="space-y-6">
      {isProposalReport && (
      <QAScoreReadinessPanel
        reportId={reportId}
        executiveSummaryVersionLabel={executiveSummaryVersionLabel}
        onBelowTargetFeedbackStep1={() => {
          setFbAutoCompute(true);
          setFbExpandPrompt(false);
          setFeedbackPlanOpen(true);
        }}
        onBelowTargetFeedbackStep2={() => {
          setFbAutoCompute(false);
          setFbExpandPrompt(true);
          setFeedbackPlanOpen(true);
        }}
      />
      )}

      <FeedbackPlanPreviewModal
        reportId={reportId}
        open={feedbackPlanOpen}
        onClose={() => setFeedbackPlanOpen(false)}
        autoComputeOnOpen={fbAutoCompute}
        expandPromptPreviewOnOpen={fbExpandPrompt}
      />

      {/* BOTH Checklists - writer + reviewer */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <QAChecklistPanel
          reportId={reportId}
          checklistType="writer"
          isAdmin={isAdmin}
        />
        <QAChecklistPanel
          reportId={reportId}
          checklistType="reviewer"
          isAdmin={isAdmin}
        />
      </div>

      <QAFormattingPanel reportId={reportId} />
      <QAGrammarPanel reportId={reportId} />
      <QAPreSubmissionPanel reportId={reportId} />

      <PIIPanelSlot reportId={reportId} />
    </div>
  );
}

// Word Count Tab Component (dynamically loaded - available for all reports)
function WordCountTabContent({ reportId, report, enableOptimizer = true }: { reportId: string; report: any; enableOptimizer?: boolean }) {
  const [WordCountTab, setWordCountTab] = React.useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Try to import from rfp-agent plugin
    // This will work if the plugin is loaded
    const loadWordCountTab = async () => {
      try {
        // Try relative path from core to plugins
        // From: tracekit/packages/core/src/components/Reports/ReportViewer.tsx
        // To: tracekit/packages/plugins/rfp-agent/src/components/WordCountTab.tsx
        // Try import using the alias defined in vite config
        // This works when running in keystone-rfp app which has the alias configured
        try {
          // Dynamic import from plugin, only available at runtime when plugin is loaded
          // Use relative path that Rollup can resolve during build
          // Path: core/src/components/Reports -> packages/plugins/rfp-agent/src/index
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/index');
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (relative path from index):', e);
        }

        try {
          // Fallback: try direct component import with relative path
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/components/WordCountTab');
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (relative path to component):', e);
        }

        try {
          // Final fallback: try alias path (only works at runtime when alias is configured)
          // Construct path dynamically to prevent Rollup from analyzing it
          // @ts-ignore - Dynamic import path
          const pluginPath = '@tracekit' + '/plugin-rfp-agent';
          const module = await import(/* @vite-ignore */ pluginPath);
          if (module && module.WordCountTab) {
            // Wrap the component to pass enableOptimizer prop
            setWordCountTab(() => (props: any) => <module.WordCountTab {...props} enableOptimizer={enableOptimizer} />);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.debug('WordCountTab import failed (alias path):', e);
        }

        // If import fails, show fallback UI
        setLoading(false);
      } catch (error) {
        console.warn('WordCountTab not available:', error);
        setLoading(false);
      }
    };

    loadWordCountTab();
  }, [enableOptimizer]);

  if (loading) {
    return (
      <Card>
        <div className="p-4 text-center text-gray-500">Loading word count tools...</div>
      </Card>
    );
  }

  if (!WordCountTab) {
    return (
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-2">Word Count Optimization</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Word count optimization features are available for all reports.
            Please ensure the RFP Agent plugin is properly loaded.
          </p>
        </div>
      </Card>
    );
  }

  return <WordCountTab reportId={reportId} report={report} enableOptimizer={enableOptimizer} />;
}

// Resume Job Button Component
function ResumeJobButton({ reportId, report, queryClient }: { reportId: string; report: any; queryClient: any }) {
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [isResuming, setIsResuming] = useState(false);

  useEffect(() => {
    // Check if job is stuck (questions incomplete)
    const totalQuestions = report?.stats?.total_questions || report?.total_questions || 0;
    const answeredQuestions = report?.stats?.answered || report?.answered_questions || 0;
    const isStuck = totalQuestions > 0 && answeredQuestions < totalQuestions && report?.status === 'generating';

    if (isStuck) {
      // Try to fetch job status
      import('../../api/reports').then(({ getReportJob }) => {
        getReportJob(reportId).then(setJobStatus).catch(() => { });
      });
    }
  }, [reportId, report]);

  const handleResumeJob = async () => {
    if (!confirm('Resume the stuck report generation job? This will continue processing remaining questions.')) {
      return;
    }

    setIsResuming(true);
    try {
      const { resumeReportJob } = await import('../../api/reports');
      await resumeReportJob(reportId);
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      alert('Job resumed successfully. It will continue processing remaining questions.');
    } catch (error: any) {
      console.error('Failed to resume job:', error);
      alert(`Failed to resume job: ${error.message || 'Unknown error'}`);
    } finally {
      setIsResuming(false);
    }
  };

  const totalQuestions = report?.stats?.total_questions || report?.total_questions || 0;
  const answeredQuestions = report?.stats?.answered || report?.answered_questions || 0;
  const isStuck = totalQuestions > 0 && answeredQuestions < totalQuestions &&
    (report?.status === 'generating' || jobStatus?.status === 'running' || jobStatus?.status === 'checkpointed');

  if (!isStuck) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleResumeJob}
      disabled={isResuming}
    >
      {isResuming ? 'Resuming...' : `Resume Job (${answeredQuestions}/${totalQuestions})`}
    </Button>
  );
}

export function ReportViewer({ reportId, onEdit, onClose, proposalWriteAccess }: ReportViewerProps) {
  const enableCustomSections = useFeatureFlag('enableCustomSections' as Parameters<typeof useFeatureFlag>[0]);
  const enableWordCountOptimizer = useFeatureFlag('enableWordCountOptimizer' as Parameters<typeof useFeatureFlag>[0]);
  const enableQualityAssurance = useFeatureFlag('enableQualityAssurance' as Parameters<typeof useFeatureFlag>[0]);
  const enableReviewWorkflow = useFeatureFlag('enableReviewWorkflow' as Parameters<typeof useFeatureFlag>[0]);
  const showCommentsAndReviewTab = useFeatureFlag('showCommentsAndReviewTab' as Parameters<typeof useFeatureFlag>[0]);
  const showAnalyticsTab = useFeatureFlag('showAnalyticsTab' as Parameters<typeof useFeatureFlag>[0]);
  const showActionsTab = useFeatureFlag('showActionsTab' as Parameters<typeof useFeatureFlag>[0]);
  const config = useConfig();
  const canEditProposal = proposalWriteAccess == null ? true : proposalWriteAccess.canEdit;
  const showSharingTabFlag = useFeatureFlag('showSharingTab' as Parameters<typeof useFeatureFlag>[0]);
  // When Comments, Analytics, Actions are all hidden (Keystone mode), hide Sharing unless explicitly true (handles missing/stale config)
  const keystoneReportViewerMode = !showCommentsAndReviewTab && !showAnalyticsTab && !showActionsTab;
  const showSharingTabRaw = config.customization?.featureFlags?.showSharingTab;
  const showSharingTab = keystoneReportViewerMode ? showSharingTabRaw === true : showSharingTabFlag;
  const [activeTab, setActiveTab] = useState<'content' | 'citations' | 'evidencegaps' | 'versions' | 'collaboration' | 'sharing' | 'analytics' | 'actions' | 'wordcount' | 'customsections' | 'qa' | 'staffassignments' | 'review'>('content');
  const [downloadingFormat, setDownloadingFormat] = useState<null | 'pdf' | 'docx'>(null);
  const [reportContent, setReportContent] = useState<string>('');
  const [reportV1Content, setReportV1Content] = useState<string>('');
  const [selectedCitation, setSelectedCitation] = useState<any>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [removedArtifactIds, setRemovedArtifactIds] = useState<Set<string>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState<string>('');
  const [showAddCitationModal, setShowAddCitationModal] = useState(false);
  const [addCitationSectionId, setAddCitationSectionId] = useState<string | undefined>();
  const [addCitationSectionName, setAddCitationSectionName] = useState<string | undefined>();
  const [selectedCitationIds, setSelectedCitationIds] = useState<Set<string>>(new Set()); // For bulk operations
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [citationsSubView, setCitationsSubView] = useState<'citations' | 'gaps' | null>(null); // KBF-063: click stats to switch tab view
  const tabContentRef = useRef<HTMLDivElement>(null);
  /** Ref to ReportContentTab so we can call startEditing() when user removes the stats line */
  const contentTabRef = useRef<ReportContentTabRef | null>(null);
  /** Latest report content for the stats-line delete handler (avoids stale closure) */
  const reportContentRef = useRef<string>(reportContent);
  reportContentRef.current = reportContent;
  
  // Handle URL parameter for initial tab selection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['content', 'citations', 'evidencegaps', 'versions', 'collaboration', 'sharing', 'analytics', 'actions', 'wordcount', 'customsections', 'qa', 'staffassignments', 'review'].includes(tabParam)) {
      setActiveTab(tabParam as any);
      console.log(`[ReportViewer] Initial tab set from URL parameter: ${tabParam}`);
    }
  }, []); // Run once on mount
  const [scrollToCitationIndex, setScrollToCitationIndex] = useState<number | null>(null); // Jump to citation in Evidence Trail when clicking [N] in content
  // Ref (not state) so updating the map never triggers a re-render.
  // Populated during the citations-tab render pass; read by the scroll effect.
  const citationNumToTabIndexRef = useRef<Map<number, number>>(new Map());
  // Artifact IDs gathered from v1 citations during the citations-tab render pass.
  // The enrichment effect reads this to fetch storage_paths for docs not in report.sections.
  const v1CitationArtifactIdsRef = useRef<string[]>([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const reportViewerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  // Enriched artifact details for citations that have artifact_id but no storage_path (old reports)
  const [citationArtifactMap, setCitationArtifactMap] = useState<Record<string, { storage_path?: string | null; source_type?: string | null }>>({});

  /** Pattern to remove "Based on N questions | Avg confidence: X% | Y unique sources" line from markdown (use with .test() per line, no /g) */
  const statsLinePattern = /Based on \d+ questions? \| Avg confidence:\s*[\d.]+%\s*\| \d+ unique sources/i;

  // Switch to Citations tab and scroll to citation by index (used by click and by URL hash)
  const goToCitationByIndex = React.useCallback((index: string | null) => {
    if (index == null || index === '') return;
    setActiveTab('citations');
    setCitationsSubView('citations');
    const scrollToCitation = () => {
      const target = document.getElementById(`citation-${index}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    requestAnimationFrame(() => setTimeout(scrollToCitation, 150));
  }, []);

  // URL hash #citation-N: open Citations tab and scroll once, then clear hash so it doesn't keep pulling focus back
  useEffect(() => {
    const scrollToCitationWithRetry = (index: string) => {
      const tryScroll = (attempt: number) => {
        const target = document.getElementById(`citation-${index}`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (attempt < 5) {
          setTimeout(() => tryScroll(attempt + 1), 300);
        }
      };
      requestAnimationFrame(() => setTimeout(() => tryScroll(0), 150));
    };
    const clearHash = () => {
      if (typeof window === 'undefined') return;
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', url);
    };
    const readHash = () => {
      if (typeof window === 'undefined') return;
      const match = window.location.hash?.match(/^#citation-(\d+)$/i);
      if (!match) return;
      const index = match[1];
      setActiveTab('citations');
      setCitationsSubView('citations');
      scrollToCitationWithRetry(index);
      clearHash();
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  // Citation link click/key: switch to Citations tab and scroll (no hash set so user can navigate freely).
  // Stats line delete (×): remove that line, save (create version), no editor open.
  // Attach to document in capture phase so the first click works even before tabContentRef is set or content has loaded.
  // Handler always reads reportContentRef.current so we never need reportContent in deps.
  useEffect(() => {
    const handleClick = async (e: MouseEvent) => {
      const deleteBtn = (e.target as Element)?.closest?.('.report-stats-line-delete');
      if (deleteBtn) {
        if (!tabContentRef.current?.contains(deleteBtn)) return;
        e.preventDefault();
        e.stopPropagation();
        const wrapper = deleteBtn.closest('.report-stats-line');
        const occurrenceStr = wrapper?.getAttribute('data-stats-line-occurrence');
        const occurrenceIndex = occurrenceStr != null ? parseInt(occurrenceStr, 10) : 0;
        const currentContent = reportContentRef.current;
        if (!currentContent || !currentContent.trim()) return;
        const lines = currentContent.split('\n');
        let matchCount = 0;
        const newLines = lines.filter((line) => {
          if (!statsLinePattern.test(line.trim())) return true;
          if (matchCount === occurrenceIndex) {
            matchCount++;
            return false;
          }
          matchCount++;
          return true;
        });
        const newContent = newLines.join('\n');
        if (newContent === currentContent) return;
        setReportContent(newContent);
        reportContentRef.current = newContent;
        try {
          const { saveReportContent } = await import('../../api/reports');
          const result = await saveReportContent(reportId, newContent, 'v2');
          queryClient.invalidateQueries({ queryKey: ['report', reportId] });
          const versionToSelect = result?.version != null && String(result.version).startsWith('v')
            ? String(result.version)
            : result?.current_version != null
              ? `v${result.current_version}`
              : null;
          if (versionToSelect) {
            setSelectedVersion(versionToSelect);
          }
          alert(`Stats lines removed and saved as ${result.version} successfully.`);
        } catch (err: any) {
          console.error('Failed to save after stats line remove:', err);
          alert(`Removed locally but save failed: ${err.message || 'Unknown error'}.`);
        }
        return;
      }
      const link = (e.target as Element)?.closest?.('.report-citation-link');
      if (!link || !tabContentRef.current?.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
      goToCitationByIndex(link.getAttribute('data-citation-index'));
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const link = (e.target as Element)?.closest?.('.report-citation-link');
      if (!link || !tabContentRef.current?.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
      goToCitationByIndex(link.getAttribute('data-citation-index'));
    };
    document.addEventListener('click', handleClick as (e: MouseEvent) => void, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('click', handleClick as (e: MouseEvent) => void, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [goToCitationByIndex, reportId, queryClient]);

  // When user clicks a citation number in content (ReportContentTab), switch to Citations tab and scroll to that citation in Evidence Trail
  const handleCitationClick = useCallback((citationIndex: number) => {
    setActiveTab('citations');
    setCitationsSubView('citations');
    setScrollToCitationIndex(citationIndex);
  }, []);

  // After Citations tab is visible, scroll to the citation card with id citation-{scrollToCitationIndex}.
  // Uses citationNumToTabIndexRef to remap LLM-generated numbers to de-duplicated tab indices.
  useEffect(() => {
    if (activeTab !== 'citations' || scrollToCitationIndex == null) return;
    const mappedIndex = citationNumToTabIndexRef.current.get(scrollToCitationIndex) ?? scrollToCitationIndex;
    const id = `citation-${mappedIndex}`;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Briefly highlight the card so the user knows which one was found
        el.classList.add('ring-2', 'ring-blue-400');
        window.setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 1500);
      }
      setScrollToCitationIndex(null);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [activeTab, scrollToCitationIndex]);

  // Fetch report metadata; poll every 5 s while the report has no sections yet (still generating)
  const { data: report, isLoading } = useQuery<Report>({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      const isGenerating = d.source_type === 'ai' && (d.total_questions ?? 0) === 0 && (d.answered_questions ?? 0) === 0;
      return isGenerating ? 5000 : false;
    },
  });

  // Fetch project artifacts to know which .pdf/.docx files are available (show only those in Evidence Trail)
  const { data: artifactsData } = useQuery({
    queryKey: ['artifacts', report?.project_id],
    queryFn: () => listArtifacts(report!.project_id),
    enabled: !!report?.project_id,
  });
  const availableArtifactFilenames = useMemo(
    () => new Set((artifactsData?.artifacts ?? []).map((a: { filename: string }) => a.filename)),
    [artifactsData]
  );

  // Back-to-top: show button when user has scrolled down; scroll the same container on click.
  // Must run after report is loaded so reportViewerRef is attached (ref is on the div that only renders when !isLoading && report).
  const BACK_TO_TOP_THRESHOLD = 200;

  function findScrollContainer(): HTMLElement | null {
    const el = reportViewerRef.current;
    if (!el) return null;
    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent && scrollParent !== document.body) {
      const { overflowY } = getComputedStyle(scrollParent);
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return scrollParent;
      }
      scrollParent = scrollParent.parentElement;
    }
    return null;
  }

  useLayoutEffect(() => {
    const scrollEl = findScrollContainer();
    const handler = () => {
      const top = scrollEl ? scrollEl.scrollTop : (typeof window !== 'undefined' ? window.scrollY : 0);
      setShowBackToTop(top > BACK_TO_TOP_THRESHOLD);
    };
    handler();
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handler, { passive: true });
      return () => scrollEl.removeEventListener('scroll', handler);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handler, { passive: true });
      return () => window.removeEventListener('scroll', handler);
    }
  }, [reportId, report]); // Re-run when report loads so ref is set and we attach to the real scroll container (main)

  const scrollToTop = useCallback(() => {
    const scrollEl = findScrollContainer();
    if (scrollEl) {
      scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const isProposalReport =
    report?.report_type === 'rfp_proposal' ||
    (report?.report_type === 'custom' && (report?.meta as { report_type_override?: string })?.report_type_override === 'rfp_proposal');
  const { data: reportTemplateData } = useQuery({
    queryKey: ['report', reportId, 'template', report?.project_id],
    queryFn: async () => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      const data = await api.get<ReportTemplateResponse>(
        `/api/opportunities/reports/${reportId}/template`,
        { params: { project_id: report?.project_id } }
      );
      return data;
    },
    enabled:
      !!reportId &&
      !!report?.project_id &&
      selectedVersion === 'v2' &&
      isProposalReport === true,
    staleTime: 60_000,
  });

  const prevExecutiveSummaryCvRef = useRef<number | null>(null);

  // Keep Report Content picker aligned with PATCH /regenerate & save flows — when meta.current_version
  // increments, upgrade to latest markdown unless the user is still loading (selectedVersion unset).
  React.useEffect(() => {
    if (!report) return;
    const raw = report.meta?.current_version;
    const cv =
      typeof raw === 'number' && raw >= 1 ? raw : 2;

    const prevTracked = prevExecutiveSummaryCvRef.current;

    if (selectedVersion == null) {
      setSelectedVersion(`v${cv}`);
      prevExecutiveSummaryCvRef.current = cv;
      return;
    }

    if (prevTracked != null && cv > prevTracked) {
      setSelectedVersion(`v${cv}`);
    }
    prevExecutiveSummaryCvRef.current = cv;
  }, [report, report?.meta?.current_version, selectedVersion]);

  // Initialize editedName when report loads or name changes
  React.useEffect(() => {
    if (report && !isEditingName) {
      const reportName = report.name || report.meta?.name || report.client_name;
      setEditedName(reportName);
    }
  }, [report?.name, report?.meta?.name, report?.client_name, isEditingName]);

  // Track view
  React.useEffect(() => {
    if (reportId) {
      trackReportView(reportId).catch(err => console.error('Failed to track view:', err));
    }
  }, [reportId]);

  // Load report content based on selected version
  React.useEffect(() => {
    if (reportId) {
      const versionToLoad = selectedVersion || (report?.meta?.current_version ? `v${report.meta.current_version}` : 'v2');

      // Load selected version for main content display
      const loadVersion = async () => {
        try {
          if (versionToLoad === 'v1') {
            const content = await downloadReportV1(reportId);
            setReportContent(content);
          } else if (versionToLoad === 'v2') {
            const content = await downloadReportV2(reportId);
            setReportContent(content);
          } else {
            const { getAPIClient } = await import('../../api/client');
            const client = getAPIClient();
            const content = await client.get<string>(`/reports/${reportId}/${versionToLoad}.md`, {
              responseType: 'text',
              params: { view: 'true' },
            } as any);
            setReportContent(content);
          }
        } catch (err) {
          console.error(`Failed to load ${versionToLoad} report content:`, err);
        }
      };

      loadVersion();

      // Always load v1 for citation extraction
      downloadReportV1(reportId)
        .then(content => {
          setReportV1Content(content);
        })
        .catch(err => console.error('Failed to load v1 report content:', err));
    }
  }, [reportId, selectedVersion, report?.meta?.current_version, report]);

  // Load v1 when Evidence Gaps tab is selected and v1 not yet loaded
  React.useEffect(() => {
    if (activeTab === 'evidencegaps' && reportId && !reportV1Content) {
      downloadReportV1(reportId)
        .then(content => setReportV1Content(content))
        .catch(err => console.error('Failed to load v1 for Evidence Gaps:', err));
    }
  }, [activeTab, reportId, reportV1Content]);

  // Batch-enrich citations with artifact details (storage_path, source_type) when Citations tab opens.
  // Covers two sources:
  //   1. report.sections — v2 section citations (may already carry storage_path directly)
  //   2. v1CitationArtifactIdsRef — artifact IDs gathered from the v1 markdown citation list
  //      (needed so the isUploadedRfpDoc filter can evaluate storage_path for all 75+ v1 sources)
  React.useEffect(() => {
    if (activeTab !== 'citations') return;
    const directUpdates: Record<string, { storage_path?: string | null; source_type?: string | null }> = {};
    const missing: string[] = [];

    // 1. Section citations (may already carry storage_path)
    if (report?.sections) {
      for (const section of report.sections as any[]) {
        for (const c of (section.citations || []) as any[]) {
          if (!c.artifact_id || citationArtifactMap[c.artifact_id]) continue;
          if (c.storage_path) {
            directUpdates[c.artifact_id] = { storage_path: c.storage_path, source_type: c.source_type || null };
          } else {
            missing.push(c.artifact_id);
          }
        }
      }
    }

    // 2. v1 citation artifact IDs not yet enriched (populated by the IIFE during render)
    for (const id of v1CitationArtifactIdsRef.current) {
      if (!citationArtifactMap[id] && !directUpdates[id] && !missing.includes(id)) {
        missing.push(id);
      }
    }

    const updates = { ...directUpdates };
    if (missing.length === 0) {
      if (Object.keys(updates).length > 0) setCitationArtifactMap(prev => ({ ...prev, ...updates }));
      return;
    }
    const uniqueIds = [...new Set(missing)];
    Promise.allSettled(uniqueIds.map(id => getArtifact(id))).then(results => {
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          updates[uniqueIds[i]] = { storage_path: r.value.storage_path, source_type: r.value.source_type };
        }
      });
      if (Object.keys(updates).length > 0) {
        setCitationArtifactMap(prev => ({ ...prev, ...updates }));
      }
    });
  }, [activeTab, report?.sections]);

  // Secondary enrichment: fetch storage_paths for v1 citation artifact IDs that became available
  // after the primary effect already fired (e.g. reportV1Content finished loading after tab open).
  React.useEffect(() => {
    if (activeTab !== 'citations') return;
    const ids = v1CitationArtifactIdsRef.current.filter(id => !citationArtifactMap[id]);
    if (ids.length === 0) return;
    const uniqueIds = [...new Set(ids)];
    Promise.allSettled(uniqueIds.map(id => getArtifact(id))).then(results => {
      const updates: Record<string, { storage_path?: string | null; source_type?: string | null }> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          updates[uniqueIds[i]] = { storage_path: r.value.storage_path, source_type: r.value.source_type };
        }
      });
      if (Object.keys(updates).length > 0) {
        setCitationArtifactMap(prev => ({ ...prev, ...updates }));
      }
    });
  // Intentionally re-runs whenever reportV1Content changes so that late-loading v1 content
  // triggers storage_path enrichment for newly discovered artifact IDs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reportV1Content]);

  // Parsed unique recommendation count from v1 (for legacy reports where API has total question count)
  const parsedEvidenceGapsCount = React.useMemo(
    () => (reportV1Content ? getParsedEvidenceGapsCount(reportV1Content) : null),
    [reportV1Content]
  );
  const displayEvidenceGapsCount =
    parsedEvidenceGapsCount ??
    report?.stats?.evidence_gaps ??
    report?.evidence_gaps_count ??
    (typeof report?.evidence_gaps === 'number' ? report.evidence_gaps : 0);

  const reportViewerTabs = useMemo(() => {
    const MANDATORY_IDS = ['content', 'citations', 'staffassignments', 'qa', 'review'] as const;
    const mandatoryIndex = (id: string): number | undefined => {
      const i = MANDATORY_IDS.indexOf(id as (typeof MANDATORY_IDS)[number]);
      return i >= 0 ? i + 1 : undefined;
    };
    const baseTabs = [
      { id: 'content', label: 'Report Content', icon: FileText },
      { id: 'citations', label: 'Citations & Evidence', icon: BookOpen },
      ...(enableCustomSections ? [{ id: 'customsections', label: 'Custom Sections', icon: Plus }] : []),
      { id: 'staffassignments', label: 'Staff Assignments', icon: Users },
      ...(enableQualityAssurance ? [{ id: 'qa', label: 'Quality Assurance', icon: ClipboardCheck }] : []),
      ...(enableReviewWorkflow ? [{ id: 'review', label: 'Review Workflow', icon: Shield }] : []),
      ...(showActionsTab ? [{ id: 'actions', label: 'Actions', icon: GitBranch, badge: displayEvidenceGapsCount }] : []),
      ...(showCommentsAndReviewTab ? [{ id: 'collaboration', label: 'Comments & Review', icon: MessageSquare }] : []),
      ...(showSharingTab ? [{ id: 'sharing', label: 'Sharing', icon: Share2 }] : []),
      ...(showAnalyticsTab ? [{ id: 'analytics', label: 'Analytics', icon: TrendingUp }] : []),
      { id: 'evidencegaps', label: 'Evidence Gaps', icon: AlertTriangle, badge: displayEvidenceGapsCount },
      { id: 'versions', label: 'Version History', icon: History },
      { id: 'wordcount', label: 'Word Count', icon: Scissors },
    ];
    return baseTabs.map((tab) => ({
      ...tab,
      mandatoryNum: mandatoryIndex(tab.id),
    }));
  }, [
    enableCustomSections,
    enableQualityAssurance,
    enableReviewWorkflow,
    showActionsTab,
    showCommentsAndReviewTab,
    showSharingTab,
    showAnalyticsTab,
    displayEvidenceGapsCount,
  ]);

  const reportViewerTabsWatchKey = useMemo(
    () => reportViewerTabs.map((t) => t.id).join(','),
    [reportViewerTabs],
  );

  // Convert markdown to HTML (proper implementation)
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';

    let html = markdown;

    // Clean response artifacts: don't show literal backslashes, \_, or --- in content
    html = html.replace(/\\_/g, '_');
    html = html.replace(/(\\?)---/g, ''); // remove --- or \---
    html = html.replace(/\\+/g, '');      // remove remaining backslashes (e.g. \\ or \\\)

    // V2 executive summary (Rendered view) must not show any citation markers in visible content.
    // Citations remain available via the "Citations & Evidence" tab.
    if (selectedVersion === 'v2' && activeTab === 'content') {
      // Remove bracketed filename citations like [Source: foo.pdf] (also removed later, but do it early)
      html = html.replace(/\[\s*Source\s*:\s*[^\]]+\]/gi, '');
      // Remove bracketed numeric citations: [Source 19], [Sources 19, 24], [Source: 19], [Source: Source 4]
      html = html.replace(/\[\s*Sources?\s*:?\s*(?:Source\s*)?\d+(?:\s*,\s*(?:Source\s*)?\d+)*\s*\]/gi, '');
      // Remove bare numeric brackets: [19]
      html = html.replace(/\[\s*\d+\s*\]/g, '');
      // Remove plain-text numeric citations: "Source 19" / "Sources 19, 24"
      html = html.replace(/\bSources?\s+\d+(?:\s*,\s*\d+)*\b/gi, '');
      // Remove lines that start with "Sources:" (fallback artefact)
      html = html.replace(/^\s*Sources\s*:\s*.*$/gim, '');

      // Clean punctuation/spacing artefacts created by removals
      html = html.replace(/\s+([,.;:])/g, '$1');
      html = html.replace(/\(\s*:\s*/g, '(');
      html = html.replace(/;\s*:\s*/g, '; ');
      html = html.replace(/,\s*:\s*/g, ', ');
      html = html.replace(/\s*\.(pdf|docx?|xlsx?|pptx?)\]\s*/gi, ' ');
      html = html.replace(/[ \t]{2,}/g, ' ');

      // Filter Compliance Matrix markdown table rows to show only true criterion IDs (e.g., G1/S2/C1/E3)
      // This removes internal/template rows like staff_section, TEAM_CORE_01, COMPANY-PP-1, and numeric staff slots.
      const lines = html.split('\n');
      const out: string[] = [];
      const criterionIdRe = /^[A-Z]{1,3}\d{1,3}$/;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        out.push(line);
        const lower = line.toLowerCase();
        const isHeader =
          line.includes('|') &&
          lower.includes('criterion') &&
          lower.includes('original') &&
          lower.includes('requirement');
        if (!isHeader) continue;
        if (i + 1 >= lines.length) break;
        const sep = lines[i + 1];
        if (!(sep.includes('|') && sep.includes('-'))) continue;
        out.push(sep);
        i += 1;
        while (i + 1 < lines.length) {
          const row = lines[i + 1];
          if (!row.trim()) { out.push(row); i += 1; break; }
          if (!row.includes('|')) break;
          const parts = row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((p) => p.trim());
          const first = (parts[0] || '').trim();
          if (criterionIdRe.test(first)) out.push(row);
          i += 1;
        }
      }
      html = out.join('\n');
    }

    // [Source: filename.ext] or [Source: filename, p. N] → placeholder (removed at end of pipeline)
    html = html.replace(/\[Source:\s*([^\]]+)\]/g, (_m, inner) => {
      const raw = inner.trim();
      const filename = raw.replace(/,?\s*p\.\s*\d+\s*$/i, '').trim();
      return `\x01SOURCE_REF\x01${filename}\x02${raw}\x01`;
    });

    // Remove plain "Source: filename.ext" or "Source: filename.docxx" (no brackets) so they don't show
    html = html.replace(/Source:\s*[^\n\[\]]+\.(?:pdf|docx?x?|xlsx?|txt|csv|pptx?)(?:\s*,\s*p\.\s*\d+)?\s*/gi, '');

    // Turn "N. **Title**" section lines into ## headings so they don't become numbered <ol><li> (no "1. 1. 1.")
    html = html.replace(/^\s*\d+\.\s+\*\*(.+?)\*\*\s*$/gm, '## $1');

    // Strip meaningless numbering from whole-line **N. Title** (no leading "N. ") so they render as **Title** only
    html = html.replace(/^(\s*)\*\*\d+\.\s*(.+?)\*\*(\s*)$/gm, '$1**$2**$3');

    // Escape HTML first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // 1. Clean up small amounts of subtitle text between headings and requirement blocks
    // ONLY match when heading is immediately followed by 1-3 short lines (< 80 chars each) then **Requirement**
    // This prevents matching across large content blocks or tables
    html = html.replace(/^(##\s+[^\n]+)\n((?:[^\n]{0,80}\n){1,3})(\*\*Requirement\*\*)/gm, (match, heading, intermediate, req) => {
      // Only remove if intermediate text looks like subtitle (doesn't start with |, *, or #)
      if (!/^[\|\*#]/.test(intermediate.trim())) {
        return `${heading}\n\n${req}`;
      }
      return match;
    });

    // 2. Clean requirement blocks: remove duplicate title/label at start of requirement content
    // Remove lines like "M1", "M4", "Licensed/Certified Firm" that appear right after **Requirement**:
    html = html.replace(/(\*\*Requirement\*\*:\s*)([A-Z][^\n]*(?:\n(?!\n)[^\n]*)*?)(?=\n\n|\n\*\*[^*]|\n-)/gm, (match, prefix, content) => {
      const lines = content.split('\n');
      // Skip first line if it's just a label (single word or short code like "M1", "Licensed/Certified Firm")
      if (lines.length > 1 && lines[0].trim().length < 100 && !lines[0].includes('must')) {
        return prefix + lines.slice(1).join('\n');
      }
      return match;
    });

    // 3. Escape asterisks in source citations to prevent italic conversion
    // Match patterns like *Document Name.docx* but NOT **bold**
    // This must happen before italic conversion
    html = html.replace(/(?<!\*)\*([^*\n]+\.(docx|pdf|xlsx|ppt|doc|txt|csv))[,\.\)]?(?!\*)/gi, (match, filename, ext) => {
      // Check if this looks like a source citation (has document extension)
      return `\\*${filename}${match.slice(filename.length)}`;
    });

    // 4. Clean requirement text: join multiline requirements into single paragraph
    html = html.replace(/(\*\*Requirement\*\*:\s*)([^\n]*(?:\n(?!\n)[^\n]*)*)/g, (match, prefix, content) => {
      // Join all lines with spaces, remove extra whitespace
      const cleaned = content.split('\n').join(' ').replace(/\s+/g, ' ').trim();
      return prefix + cleaned;
    });

    // Code blocks (must be done before inline code)
    html = html.replace(/```([^`]+)```/g, '<pre class="bg-gray-100 p-4 rounded-lg overflow-x-auto my-4 border border-gray-300"><code class="text-sm font-mono">$1</code></pre>');

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono text-gray-800">$1</code>');

    // Markdown tables - convert pipe-separated tables to HTML tables
    // Process line-by-line to handle tables with many rows
    const tableLines: string[] = [];
    let inTable = false;
    let currentTable: string[] = [];
    
    const processTable = (rows: string[]): string => {
      if (rows.length < 2) return rows.join('\n');
      
      // First line is header
      const headerLine = rows[0];
      const headerCells = headerLine.split('|').filter(cell => cell.trim().length > 0).map(cell => cell.trim());
      
      // Check if second line is separator (contains dashes/colons)
      const secondLine = rows[1];
      const isSeparator = /^[\s|:\-]*$/.test(secondLine);
      
      if (!isSeparator || headerCells.length === 0) {
        return rows.join('\n');
      }
      
      // All remaining lines are data rows
      const dataRows = rows.slice(2);
      
      // Build HTML table
      let tableHtml = '<table class="w-full border-collapse border border-theme-border my-4 text-sm">';
      
      // Header
      tableHtml += '<thead class="bg-theme-surface-hover">';
      tableHtml += '<tr>';
      headerCells.forEach(cell => {
        tableHtml += `<th class="border border-theme-border px-3 py-2 font-semibold text-theme-text text-left">${cell}</th>`;
      });
      tableHtml += '</tr></thead>';
      
      // Rows
      tableHtml += '<tbody>';
      dataRows.forEach((row, idx) => {
        const cells = row.split('|').filter(cell => cell.trim().length > 0).map(cell => cell.trim());
        if (cells.length === 0) return; // Skip empty rows
        
        const bgClass = idx % 2 === 0 ? 'bg-theme-paper' : 'bg-gray-50 dark:bg-gray-800/50';
        tableHtml += `<tr class="${bgClass}">`;
        cells.forEach(cell => {
          tableHtml += `<td class="border border-theme-border px-3 py-2 text-theme-text">${cell}</td>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</tbody></table>';
      
      return tableHtml;
    };
    
    // Split by lines and process tables
    const htmlLines = html.split('\n');
    for (let i = 0; i < htmlLines.length; i++) {
      const line = htmlLines[i];
      
      // Check if this line is part of a table (starts and ends with |)
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        if (!inTable) {
          inTable = true;
          currentTable = [];
        }
        currentTable.push(line.trim());
      } else {
        // Not a table line
        if (inTable && currentTable.length > 0) {
          // Process the accumulated table
          tableLines.push(processTable(currentTable));
          currentTable = [];
          inTable = false;
        }
        // Add non-table line
        if (line.trim() !== '') {
          tableLines.push(line);
        }
      }
    }
    
    // Don't forget the last table if document ends with one
    if (inTable && currentTable.length > 0) {
      tableLines.push(processTable(currentTable));
    }
    
    html = tableLines.join('\n');

    // Headers (must be on new lines) - reduced spacing
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-gray-800 mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-theme-text mt-5 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-theme-text mt-6 mb-4 pb-3 border-b-3 border-blue-500 dark:border-blue-400">$1</h1>');

    // Bold (before italic to handle **)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');

    // Italic
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700">$1</em>');

    // Citation anchors that are still in raw markdown form (`[N](#citation-N)`,
    // `[Source N](#citation-N)`) get normalized to bracketed-source syntax FIRST
    // so the citation-pill replacement below picks them up. Otherwise the
    // generic markdown-link pass would render them as plain blue links AND we
    // would later see literal `(#citation-N)` text next to the badge.
    html = html.replace(
      /\[(?:Source\s+)?(\d+)\]\s*\(\s*#citation-\d+\s*\)/gi,
      '[Source $1]'
    );

    // Markdown links [text](url) (so we don't turn them into citation links).
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline font-medium">$1</a>');

    // Strip dangling raw `(#citation-N)` fragments that some exports leave next
    // to the human-readable citation. These fragments add no value once the
    // citation pill is rendered and only appear as visual noise.
    html = html.replace(/\s*\(\s*#citation-\d+\s*\)/gi, '');

    // Citations: [Sources N,M,...] | [Source N,M,...] | [Source N] | [N] and plain "Source N" / "Sources N, M" → clickable links to Citations & Evidence Trail
    // Use <span> (not <a href="#citation-N">) so the URL does not get a hash; click handler switches tab and scrolls.
    // Display text keeps "Source N" / "Sources N, M" so the citation is clearly labeled.
    const citationPill = (num: string, displayText: string) =>
      `<span class="report-citation-link ml-0.5 bg-blue-500 text-white dark:text-blue-400 font-medium cursor-pointer hover:bg-blue-600 focus:outline-none rounded px-0.5 inline-flex align-middle" data-citation-index="${num}" role="button" tabindex="0" title="Jump to citation ${num} in Evidence Trail">${displayText}</span>`;

    const applyCitationMatch = (
      _: string,
      sourcesNums: string | undefined,
      sourceNumsSingular: string | undefined,
      sourceNum: string | undefined,
      bareNum: string | undefined
    ): string => {
      if (sourcesNums !== undefined) {
        const indices = sourcesNums.split(',').map((n: string) => n.trim()).filter(Boolean);
        return indices.map((num: string) => citationPill(num, `Source ${num}`)).join(', ');
      }
      if (sourceNumsSingular !== undefined) {
        const indices = sourceNumsSingular.split(',').map((n: string) => n.trim()).filter(Boolean);
        return indices.map((num: string) => citationPill(num, `Source ${num}`)).join(', ');
      }
      if (sourceNum !== undefined) return citationPill(sourceNum, `Source ${sourceNum}`);
      if (bareNum !== undefined) return citationPill(bareNum, `Source ${bareNum}`);
      return _;
    };

    // Escaped brackets (e.g. \[Source 3, 5, 29\] from some exports) — handle first so all numbers become pills
    html = html.replace(
      /\\\[Sources\s+([\d,\s]+)\\\]|\\\[Source\s+([\d,\s]+)\\\]|\\\[Source\s+(\d+)\\\]|\\\[(\d+)\\\]/gi,
      applyCitationMatch
    );

    // Bracketed forms: [Sources N,M,...] | [Source N,M,...] | [Source N] | [N] — display as "Source N" / "Sources N, M"
    html = html.replace(
      /\[Sources\s+([\d,\s]+)\]|\[Source\s+([\d,\s]+)\]|\[Source\s+(\d+)\]|\[(\d+)\]/gi,
      applyCitationMatch
    );

    // Plain text (no brackets): "Source 19", "Source 21", "Sources 19, 21, 27", "Source 3, 5, 29, 34" → same pills (skip if inside <a>)
    const notInsideAnchor = (str: string, matchIndex: number) => {
      const before = str.slice(0, matchIndex);
      const lastOpen = before.lastIndexOf('<a ');
      const lastClose = before.lastIndexOf('</a>');
      return lastOpen <= lastClose; // not inside <a>...</a>
    };
    html = html.replace(/\bSources\s+([\d,\s]+)\b/gi, (match, nums: string, offset: number) => {
      if (!notInsideAnchor(html, offset)) return match;
      const indices = nums.split(',').map((n: string) => n.trim()).filter(Boolean);
      return indices.map((num: string) => citationPill(num, `Source ${num}`)).join(', ');
    });
    // "Source N, M, ..." (singular, multiple numbers) — run before single-number so "Source 3, 5, 29" becomes all pills
    html = html.replace(/\bSource\s+([\d,\s]+)\b/gi, (match, nums: string, offset: number) => {
      if (!notInsideAnchor(html, offset)) return match;
      const indices = nums.split(',').map((n: string) => n.trim()).filter(Boolean);
      return indices.map((num: string) => citationPill(num, `Source ${num}`)).join(', ');
    });
    html = html.replace(/\bSource\s+(\d+)\b/gi, (match, num: string, offset: number) =>
      notInsideAnchor(html, offset) ? citationPill(num, `Source ${num}`) : match
    );

    // Unordered lists - FIXED: Reset list state properly to prevent staircase indentation
    const lines = html.split('\n');
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    let processedLines: string[] = [];
    let listIndentLevel = 0; // Track nesting level

    lines.forEach(line => {
      const trimmed = line.trim();
      const leadingSpaces = line.length - line.trimStart().length;
      const currentIndent = Math.floor(leadingSpaces / 2); // Approximate indent level

      // Check for list items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        // Close previous list if different type or different indent
        if (inList && (listType !== 'ul' || currentIndent !== listIndentLevel)) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }

        if (!inList) {
          processedLines.push('<ul class="list-disc list-inside ml-4 my-3 space-y-1">');
          inList = true;
          listType = 'ul';
          listIndentLevel = currentIndent;
        }
        processedLines.push(`<li class="text-gray-700 dark:text-gray-300">${trimmed.substring(2)}</li>`);
      } else if (trimmed.match(/^\d+\.\s/)) {
        // Render "N. paragraph" as bullets (ul), not numbered list (ol), to avoid "1. 1. 1." when content uses "1." for every item
        if (inList && (listType !== 'ul' || currentIndent !== listIndentLevel)) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }

        if (!inList) {
          processedLines.push('<ul class="list-disc list-inside ml-4 my-3 space-y-1">');
          inList = true;
          listType = 'ul';
          listIndentLevel = currentIndent;
        }
        processedLines.push(`<li class="text-gray-700 dark:text-gray-300">${trimmed.replace(/^\d+\.\s/, '')}</li>`);
      } else {
        // Close list if we're leaving list context
        if (inList) {
          processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
          inList = false;
          listType = null;
          listIndentLevel = 0;
        }

        if (trimmed === '') {
          // Skip empty lines to reduce spacing (don't add <br/>)
          // This prevents excessive spacing in rendered view
        } else if (!trimmed.startsWith('<')) {
          // Regular paragraph - reduced spacing
          processedLines.push(`<p class="text-theme-text mb-3 leading-relaxed">${line}</p>`);
        } else {
          processedLines.push(line);
        }
      }
    });

    // Close any remaining list
    if (inList) {
      processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
    }

    let finalHtml = processedLines.join('\n');

    // Stats line: "Based on N questions | Avg confidence: X% | Y unique sources" → bold, hover, and deletable (each with occurrence index for single-remove)
    const statsLineRegex = /Based on \d+ questions? \| Avg confidence:\s*[\d.]+%\s*\| \d+ unique sources/gi;
    let statsLineOccurrence = 0;
    finalHtml = finalHtml.replace(statsLineRegex, (match) => {
      const idx = statsLineOccurrence++;
      return `<span class="report-stats-line font-bold text-theme-text hover:underline inline-flex items-center gap-2 flex-wrap align-middle" data-report-stats-line data-stats-line-occurrence="${idx}">` +
        `<span class="report-stats-line-text">${match}</span>` +
        `<button type="button" class="report-stats-line-delete ml-1 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 hover:text-red-600 dark:hover:text-red-400 text-sm leading-none cursor-pointer" aria-label="Remove this stats line" title="Click to remove once you have reviewed the AI generated content">×</button>` +
        `</span>`;
    });

    // Remove [Source: ...] citations entirely (no "Source: filename" or link shown)
    finalHtml = finalHtml.replace(/\x01SOURCE_REF\x01[^\x02]+\x02[^\x01]*\x01/g, '');

    return finalHtml;
  };

  // Mock versions data (will be replaced with real API)
  // Build versions array from report.versions and meta (includes v1, v2, and all higher versions)
  const versions: ReportVersion[] = (() => {
    const versionList: ReportVersion[] = [];

    // Get current version number
    const currentVersionNum = report?.meta?.current_version || 2;

    // Add v1 and v2 - they always exist for AI-generated reports
    if (report) {
      // v1 always exists for AI reports
      versionList.push({
        version: 1,
        status: currentVersionNum === 1 ? 'current' : 'archived',
        changes: 'V1 - Research Draft',
        created_by: 'System (Auto-generated)',
        created_at: report.created_at || new Date().toISOString()
      });
      // v2 always exists for AI reports
      versionList.push({
        version: 2,
        status: currentVersionNum === 2 ? 'current' : 'archived',
        changes: 'V2 - Executive Summary',
        created_by: 'System (Auto-generated)',
        created_at: report.created_at || new Date().toISOString()
      });
    }

    // Add higher versions (v3, v4, etc.) from meta
    if (report?.meta && typeof report.meta === 'object') {
      const meta = report.meta as Record<string, any>;

      // Find all version numbers from blob_path keys
      const foundVersions = new Set<number>();
      Object.keys(meta).forEach(key => {
        const match = key.match(/^v(\d+)_blob_path$/);
        if (match) {
          const versionNum = parseInt(match[1]);
          if (versionNum >= 3) {
            foundVersions.add(versionNum);
          }
        }
      });

      // Add found versions
      Array.from(foundVersions).forEach(v => {
        const changeSummary = meta[`v${v}_change_summary`] || 'Manual edit';
        const createdBy = meta[`v${v}_created_by`] || 'Unknown';
        const createdAt = meta[`v${v}_created_at`] || report.updated_at || report.created_at || new Date().toISOString();

        versionList.push({
          version: v,
          status: v === currentVersionNum ? 'current' : 'archived',
          changes: changeSummary,
          created_by: createdBy,
          created_at: createdAt
        });
      });
    }

    // Also add versions up to current_version if not already found
    for (let v = 3; v <= currentVersionNum; v++) {
      if (!versionList.find(vv => vv.version === v)) {
        versionList.push({
          version: v,
          status: v === currentVersionNum ? 'current' : 'archived',
          changes: 'Manual edit',
          created_by: 'Unknown',
          created_at: report?.updated_at || report?.created_at || new Date().toISOString()
        });
      }
    }

    // Sort by version number descending (newest first)
    versionList.sort((a, b) => b.version - a.version);

    return versionList;
  })();

  const handleDownload = async (format: 'v1' | 'v2' | 'pdf' | 'docx' | 'xlsx') => {
    if (format === 'pdf' || format === 'docx') {
      setDownloadingFormat(format);
    }
    try {
      // Export guardrails: Allow all statuses for Keystone users
      // Draft/In-review will be watermarked on backend
      const currentStatus = report?.status || 'draft';

      // PDF, DOCX, XLSX, and V2 exports are now available for all statuses
      // Draft/In-review documents will have watermarks applied automatically

      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        // Track only after confirmed success
        trackReportDownload(reportId, format).catch(err => console.warn('Failed to track download:', err));
      } else if (format === 'docx') {
        const blob = await downloadReportDOCX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        // Track only after confirmed success
        trackReportDownload(reportId, format).catch(err => console.warn('Failed to track download:', err));
      } else if (format === 'xlsx') {
        const blob = await downloadReportXLSX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${reportId.substring(0, 8)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const content = format === 'v1'
          ? await downloadReportV1(reportId)
          : await downloadReportV2(reportId);
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = (report?.name || report?.client_name || 'report')?.replace(/[^a-z0-9]/gi, '_');
        a.download = `${fileName}_${format}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      // Refresh analytics
      queryClient.invalidateQueries({ queryKey: ['report-analytics', reportId] });
    } catch (error) {
      console.error('Download failed:', error);
      addToast({
        type: 'error',
        title: 'Download failed',
        message: 'The report could not be exported. Please try again or contact support if the issue persists.',
        duration: 7000,
      });
    } finally {
      if (format === 'pdf' || format === 'docx') {
        setDownloadingFormat((prev) => (prev === format ? null : prev));
      }
    }
  };

  const handleTraceEvidence = async (evidenceId: string) => {
    try {
      // Call evidence trace API
      const { traceEvidence } = await import('../../api/evidence');
      const traceData = await traceEvidence(evidenceId);

      // Open modal with trace data (includes chunk_id and full content)
      setSelectedCitation({
        evidence_id: evidenceId,
        trace: traceData,
        chunk_id: traceData.chunk?.id,
        chunk_content: traceData.chunk?.content,
      });
    } catch (error) {
      console.error('Failed to trace evidence:', error);
      addToast({ type: 'error', title: 'Trace unavailable', message: 'Could not load evidence details. Please try again.' });
    }
  };

  const handleDownloadArtifact = async (artifactId: string, filename: string) => {
    await downloadArtifactWithToast(artifactId, filename, addToast);
  };

  const handleRemoveCitation = async (citation: any) => {
    if (!citation.artifact_id) {
      alert('Cannot remove citation: artifact ID not available');
      return;
    }

    // Find which sections use this citation
    const affectedSections = citation.sections || [citation.section || 'Unknown'];
    const sectionIds = report?.sections
      ?.filter((s: any) => affectedSections.includes(s.section_name))
      .map((s: any) => s.id) || [];

    const confirmMessage = `Remove this citation from the report?\n\n` +
      `Source: ${citation.source}\n` +
      `Affected Sections: ${affectedSections.join(', ')}\n` +
      `This will remove the citation and you can regenerate ${sectionIds.length} section(s) without this source.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await removeCitation(reportId, {
        artifact_id: citation.artifact_id,
        section_id: sectionIds.length === 1 ? sectionIds[0] : undefined
      });

      // Track removed artifact and affected sections
      setRemovedArtifactIds(prev => new Set([...prev, citation.artifact_id]));

      // Refresh report content
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });

      // Reload report content
      downloadReportV1(reportId)
        .then(content => setReportV1Content(content))
        .catch(err => console.error('Failed to reload v1:', err));
      downloadReportV2(reportId)
        .then(content => setReportContent(content))
        .catch(err => console.error('Failed to reload v2:', err));

      alert(`Citation removed successfully. ${sectionIds.length} section(s) affected. Use "Regenerate Sections" to update the report content without this source.`);
    } catch (error: any) {
      console.error('Failed to remove citation:', error);
      alert(`Failed to remove citation: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRegenerateSections = async (specificSectionIds?: string[]) => {
    if (removedArtifactIds.size === 0) {
      alert('No sources have been removed. Remove citations first before regenerating.');
      return;
    }

    if (!report?.sections || report.sections.length === 0) {
      alert('No sections found to regenerate.');
      return;
    }

    const excludedList = Array.from(removedArtifactIds);
    // Use specific section IDs if provided, otherwise regenerate all sections
    const sectionIds = specificSectionIds || report.sections.map((s: any) => s.id);

    // Get section names for confirmation
    const sectionNames = report.sections
      .filter((s: any) => sectionIds.includes(s.id))
      .map((s: any) => s.section_name)
      .join(', ');

    if (!confirm(
      `Regenerate ${sectionIds.length} section(s) excluding ${excludedList.length} source(s)?\n\n` +
      `Sections to regenerate:\n${sectionNames}\n\n` +
      `Excluded sources:\n${excludedList.slice(0, 5).map(id => id.substring(0, 8) + '...').join(', ')}${excludedList.length > 5 ? ` and ${excludedList.length - 5} more...` : ''}\n\n` +
      `This will regenerate the selected sections without using the excluded sources.`
    )) {
      return;
    }

    setIsRegenerating(true);
    try {
      const response = await regenerateSections(reportId, {
        section_ids: sectionIds,
        excluded_artifact_ids: excludedList
      });

      // Clear removed artifacts (they've been processed)
      setRemovedArtifactIds(new Set());

      // Refresh report
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });

      // Reload report content
      downloadReportV1(reportId)
        .then(content => setReportV1Content(content))
        .catch(err => console.error('Failed to reload v1:', err));
      downloadReportV2(reportId)
        .then(content => setReportContent(content))
        .catch(err => console.error('Failed to reload v2:', err));

      const partialWarning = response.sections_failed && response.sections_failed > 0
        ? `\n\nWarning: ${response.sections_failed} section(s) could not be regenerated.`
        : '';
      alert(`Successfully regenerated ${response.sections_regenerated} section(s) without the excluded sources.${partialWarning}\n\nNew stats:\n- Total Citations: ${response.new_stats.total_citations}\n- Avg Confidence: ${(response.new_stats.avg_confidence * 100).toFixed(1)}%`);
    } catch (error: any) {
      console.error('Failed to regenerate sections:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      alert(`Failed to regenerate sections: ${detail}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Report not found</p>
      </Card>
    );
  }

  if (report.source_type === 'ai' && (report.total_questions ?? 0) === 0 && (report.answered_questions ?? 0) === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">Report is still being generated</p>
          <p className="text-sm text-gray-400">This page will refresh automatically when the report is ready.</p>
        </div>
      </Card>
    );
  }

  return (
    <div ref={reportViewerRef} className="space-y-6">
      {/* Header: title on its own line; type, dates, status, export as subtitle row */}
      <Card>
        {/* Row 1: Title only (one line) */}
        <div className="flex items-center gap-2 min-w-0 w-full mb-2">
          <FileText className="h-5 w-5 shrink-0 text-blue-600" />
          {(() => {
            const reportName = report.name || report.meta?.proposal_name || report.meta?.name || report.client_name;
            const handleSaveName = async () => {
              try {
                const { updateReport } = await import('../../api/reports');
                await updateReport(reportId, { name: editedName });
                queryClient.invalidateQueries({ queryKey: ['report', reportId] });
                setIsEditingName(false);
              } catch (error: any) {
                console.error('Failed to update name:', error);
                alert(`Failed to update name: ${error.message || 'Unknown error'}`);
                setEditedName(reportName);
              }
            };
            if (isEditingName) {
              return (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="px-2 py-0.5 border border-gray-300 rounded text-lg font-semibold text-theme-text min-w-0 max-w-full flex-1"
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    else if (e.key === 'Escape') { setIsEditingName(false); setEditedName(reportName); }
                  }}
                  autoFocus
                />
              );
            }
            return (
              <h1
                className={`text-lg font-semibold text-theme-text truncate flex-1 min-w-0 ${canEditProposal ? 'cursor-pointer hover:underline' : ''}`}
                onClick={() => canEditProposal && setIsEditingName(true)}
                title={canEditProposal ? 'Click to edit name' : undefined}
              >
                {reportName}
              </h1>
            );
          })()}
        </div>
        {/* Row 2: Subtitle – type, dates, status, export buttons */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
          <span className="shrink-0 capitalize">
            {/* {report.report_type?.replace(/_/g, ' ')} */}
            {report.report_type?.replace(/_/g, ' ').replace(/\brfp\b/gi, 'RFP').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Custom'}
            </span>
          <span className="shrink-0 flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            Created: {new Date(report.created_at).toLocaleDateString()}
            {report.updated_at && (
              <>
                <span className="mx-1">·</span>
                Updated: {new Date(report.updated_at).toLocaleDateString()}
              </>
            )}
          </span>
          <Badge
            variant={
              report?.status === 'approved' ? 'success' :
                report?.status === 'submitted' ? 'success' :
                  report?.status === 'archived' ? 'info' :
                    // report?.status === 'in_review' ? 'warning' :
                      'default'
            }
            size="sm" 
            className={`shrink-0 ${report?.status === 'approved' || report?.status === 'submitted' ? 'bg-green-600 text-white dark:text-white' : report?.status === 'archived' ? 'bg-gray-600 text-white dark:text-white' : report?.status === 'in_review' ? 'bg-orange-600 text-white dark:text-white' : 'bg-gray-600 text-white dark:text-white'}`}
          >
            {report?.status ? report.status.charAt(0).toUpperCase() + report.status.slice(1).replace('_', ' ') : 'Draft'}
          </Badge>
          <div className="flex gap-2 items-center shrink-0 ml-auto">
            {(() => {
              const currentStatus = report?.status || 'draft';
              const isDraft = currentStatus === 'draft' || currentStatus === 'in_review';
              const watermarkNotice = isDraft
                ? `(Will include DRAFT watermark)`
                : '';

              return (
                <>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => handleDownload('pdf')}
                      disabled={downloadingFormat === 'pdf' || downloadingFormat === 'docx'}
                      title={watermarkNotice ? 'Download as PDF. Draft documents will have a DRAFT watermark.' : 'Download as PDF'}
                    >
                      {downloadingFormat === 'pdf' ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {downloadingFormat === 'pdf' ? 'Downloading…' : 'PDF'}
                    </Button>
                    {watermarkNotice && (
                      <div className="group relative">
                        <AlertCircle className="h-4 w-4 text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-orange-900 dark:bg-orange-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          Draft documents will include a watermark when exported
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-orange-900 dark:border-t-orange-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      onClick={() => handleDownload('docx')}
                      disabled={downloadingFormat === 'pdf' || downloadingFormat === 'docx'}
                      title={watermarkNotice ? 'Download as DOCX. Draft documents will have a DRAFT watermark.' : 'Download as DOCX'}
                    >
                      {downloadingFormat === 'docx' ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {downloadingFormat === 'docx' ? 'Downloading…' : 'DOCX'}
                    </Button>
                    {watermarkNotice && (
                      <div className="group relative">
                        <AlertCircle className="h-4 w-4 text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-orange-900 dark:bg-orange-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          Draft documents will include a watermark when exported
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-orange-900 dark:border-t-orange-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      onClick={() => handleDownload('xlsx')}
                      title={isDraft ? 'Download as XLSX. Draft documents will have a DRAFT watermark.' : 'Download as XLSX'}
                    >
                      <Download className="h-4 w-4" />
                      XLSX
                    </Button>
                    {isDraft && (
                      <div className="group relative">
                        <AlertCircle className="h-4 w-4 text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-orange-900 dark:bg-orange-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          Draft documents will include a watermark when exported
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-orange-900 dark:border-t-orange-800"></div>
                        </div>
                      </div>
                    )}
                  </div> */}
                </>
              );
            })()}
            {/* Edit button removed - use "Edit Content" button in content tab instead */}
          </div>
          {canEditProposal && <ResumeJobButton reportId={reportId} report={report} queryClient={queryClient} />}
        </div>
        {report?.status && (report.status === 'draft' || report.status === 'in_review') && (
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            ⚠️ Downloads will include a DRAFT watermark
          </p>
        )}

        {/* Template metadata (from meta.template_result.metadata when present) */}
        {report?.meta?.template_result?.metadata && (() => {
          const tm = report.meta.template_result.metadata as Record<string, any>;
          const items: { label: string; value: string | number }[] = [];
          if (tm.organization_name) items.push({ label: 'Organization', value: tm.organization_name });
          if (tm.due_date && tm.due_date !== 'N/A') items.push({ label: 'Due date', value: tm.due_date });
          if (tm.quality_score != null) items.push({ label: 'Quality score', value: `${Number(tm.quality_score).toFixed(1)}%` });
          if (tm.criteria_count != null) items.push({ label: 'Criteria', value: tm.criteria_count });
          if (tm.question_count != null) items.push({ label: 'Questions', value: tm.question_count });
          if (items.length === 0) return null;
          return (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
              {items.map(({ label, value }) => (
                <span key={label}><span className="font-medium text-gray-500 dark:text-gray-500">{label}:</span> {value}</span>
              ))}
            </div>
          );
        })()}

        {/* Stats Row - KBF-063: click Citations/Gaps to switch to Citations tab */}
        <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-theme-text">
              {report.stats?.answered || report.answered_questions || 0}/{report.stats?.total_questions || report.total_questions || 0}
            </div>
            <div className="text-sm text-gray-500">Questions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {(() => {
                const raw = report.stats?.avg_confidence ?? report.avg_confidence;
                const pct = normalizeConfidenceForDisplay(raw);
                return pct !== null ? `${pct}%` : '-';
              })()}
            </div>
            <div className="text-sm text-gray-500">Avg Confidence</div>
          </div>
          <button
            type="button"
            onClick={() => { setActiveTab('citations'); setCitationsSubView('citations'); }}
            className="text-center rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
          >
            <div className="text-2xl font-bold text-green-600">
              {report.stats?.total_citations || report.total_citations || 0}
            </div>
            <div className="text-sm text-gray-500">Citations</div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('evidencegaps')}
            className="text-center rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
          >
            <div className={`text-2xl font-bold ${displayEvidenceGapsCount === 0 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
              {displayEvidenceGapsCount}
            </div>
            <div className="text-sm text-gray-500">Evidence Gaps</div>
          </button>
        </div>
      </Card>

      {/* Tabs: scrollable with chevron affordance (same as Settings) */}
      <ScrollableTabStrip watchKey={reportViewerTabsWatchKey}>
        {reportViewerTabs.map((tab) => {
          const Icon = tab.icon;
          const num = tab.mandatoryNum;
          const isMandatory = num !== undefined;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 px-4 py-3 transition-all duration-200 whitespace-nowrap rounded-[15px] shrink-0 ${isActive
                ? 'border-blue-600 text-blue-700 dark:text-blue-300 font-semibold bg-blue-100 dark:bg-blue-900/40 shadow-sm'
                : isMandatory
                  ? 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:bg-blue-900/40 dark:hover:text-white font-medium'
                  : 'text-gray-600 hover:text-gray-900 dark:hover:bg-blue-900/40 dark:hover:text-white'
                }`}
            >
              {isMandatory && (
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${isActive
                    ? 'bg-blue-600 text-white dark:bg-blue-500 dark:text-white'
                    : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                    }`}
                  aria-label={`Step ${num}`}
                >
                  {num}
                </span>
              )}
              <Icon
                className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : ''}`}
                strokeWidth={isActive ? 2.25 : 2}
              />
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white rounded-full text-xs font-bold">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </ScrollableTabStrip>

      {/* Tab Content - content tab isolated so Edit Content button only re-renders this subtree */}
      <div ref={tabContentRef}>
        {activeTab === 'content' && (
          <ReportContentTab
            ref={contentTabRef}
            reportId={reportId}
            report={report}
            selectedVersion={selectedVersion}
            onSelectedVersionChange={setSelectedVersion}
            reportContent={reportContent}
            reportV1Content={reportV1Content}
            onReportContentChange={setReportContent}
            reportTemplateData={reportTemplateData ?? null}
            queryClient={queryClient}
            convertMarkdownToHTML={convertMarkdownToHTML}
            preprocessMarkdownWithFullRequirements={preprocessMarkdownWithFullRequirements}
            onCitationClick={handleCitationClick}
            readOnly={!canEditProposal}
            isProposalReport={isProposalReport}
          />
        )}

        {activeTab === 'citations' && (
          <Card title="Citations & Evidence Trail">
            <div className="space-y-4">
              {/* Extract all citations from v1.md content */}
              {(() => {
                let citations: any[] = [];

                // Extract citations from v1 markdown content with full context
                if (reportV1Content) {
                  // Strategy 1: Try to find Citations section
                  let citationsSectionMatch = reportV1Content.match(/## Citations\s*([\s\S]*?)(?=\n##|$)/);

                  if (citationsSectionMatch) {
                    const citationsSection = citationsSectionMatch[1];

                    // Debug logging (only in development)
                    // if (process.env.NODE_ENV === 'development') {
                    //   console.log('[ReportViewer] Citations section found, length:', citationsSection.length);
                    //   console.log('[ReportViewer] First 500 chars:', citationsSection.substring(0, 500));
                    // }

                    // Split by newlines - keep all lines including empty ones for context
                    const allLines = citationsSection.split('\n');
                    const citationsMap = new Map<number, any>();

                    // Parse citations section line by line with proper context
                    for (let i = 0; i < allLines.length; i++) {
                      const line = allLines[i];
                      const cleanLine = line.replace(/<[^>]*>/g, '').trim();

                      // Skip empty lines, HTML anchors, and markdown links
                      if (!cleanLine || cleanLine.length < 3) continue;

                      // Skip markdown citation links like [1](#citation-1) or (#citation-1)
                      if (cleanLine.match(/^\(?#citation-\d+\)?/i) || cleanLine.match(/\[.*\]\(#citation-\d+\)/)) {
                        continue;
                      }

                      // Match citation header: ### [N] filename.ext
                      // This is the primary format from citation_manager.py
                      const headerMatch = cleanLine.match(/^###\s+\[(\d+)\]\s+(.+)$/);

                      if (headerMatch) {
                        const citationNum = parseInt(headerMatch[1]);
                        let source = headerMatch[2].trim();

                        // Remove any trailing markdown or special characters
                        source = source.replace(/\s*[-\u2013\u2014].*$/, '').trim();

                        // Skip if source looks like a markdown link
                        if (source.match(/^\[.*\]\(/)) continue;

                        // Initialize citation object
                        const citation: any = {
                          index: citationNum,
                          source: source, // Full filename
                          text: '',
                          quote: '',
                          page: null,
                          confidence: undefined,
                          section: 'Research Draft',
                          evidence_id: null,
                          artifact_id: null,
                        };

                        // Look ahead for metadata lines (Page, Confidence, Preview)
                        // Increase lookahead to 10 lines to catch multi-line previews
                        for (let j = i + 1; j < Math.min(i + 11, allLines.length); j++) {
                          const nextLine = allLines[j].replace(/<[^>]*>/g, '').trim();

                          // Stop if we hit another citation header
                          if (nextLine.match(/^###\s+\[\d+\]/)) break;

                          // Skip empty lines but continue looking
                          if (!nextLine) continue;

                          // Extract Artifact ID (document traceability; show in UI)
                          const artifactIdMatch = nextLine.match(/^[-*]\s*\*\*Artifact ID:\*\*\s*(.+?)\s*$/i) ||
                            nextLine.match(/^[-*]\s*Artifact ID[:\s]+(.+?)\s*$/i);
                          if (artifactIdMatch && !citation.artifact_id) {
                            citation.artifact_id = artifactIdMatch[1].trim();
                          }
                          // Extract Chunk ID (optional)
                          const chunkIdMatch = nextLine.match(/^[-*]\s*\*\*Chunk ID:\*\*\s*(.+?)\s*$/i) ||
                            nextLine.match(/^[-*]\s*Chunk ID[:\s]+(.+?)\s*$/i);
                          if (chunkIdMatch && !citation.chunk_id) {
                            citation.chunk_id = chunkIdMatch[1].trim();
                          }
                          // Extract Page
                          const pageMatch = nextLine.match(/^[-*]\s*\*\*Page:\*\*\s*(\d+)/i) ||
                            nextLine.match(/^[-*]\s*Page[:\s]+(\d+)/i);
                          if (pageMatch && !citation.page) {
                            citation.page = parseInt(pageMatch[1]);
                          }
                          // Extract Confidence (only show when meaningful >= 15%; backend omits RRF/low scores)
                          const confMatch = nextLine.match(/^[-*]\s*\*\*Confidence:\*\*\s*([\d.]+)%?/i) ||
                            nextLine.match(/^[-*]\s*Confidence[:\s]+([\d.]+)%?/i);
                          if (confMatch && citation.confidence === undefined) {
                            const confValue = parseFloat(confMatch[1]);
                            const normalized = confValue > 1 ? confValue / 100 : confValue;
                            if (normalized >= 0.15) citation.confidence = normalized;
                          }

                          // Extract Preview/Content - this is the actual chunk content
                          // Handle multiple formats: - **Preview:** "content..." or - Preview: "content..."
                          // The content may have escaped quotes or be on multiple lines
                          if (!citation.quote) {
                            // Primary format: - **Preview:** "content..."
                            let previewMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*"((?:[^"\\]|\\.)+)"/i);

                            if (!previewMatch) {
                              // Alternative format: - **Preview:** "content... (without closing quote on same line)
                              previewMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*"((?:[^"\\]|\\.)+)/i);
                            }

                            if (!previewMatch) {
                              // Fallback: - Preview: "content..."
                              previewMatch = nextLine.match(/^[-*]\s*Preview[:\s]+"((?:[^"\\]|\\.)+)"/i);
                            }

                            if (!previewMatch) {
                              // Fallback: - Preview: "content... (without closing quote)
                              previewMatch = nextLine.match(/^[-*]\s*Preview[:\s]+"((?:[^"\\]|\\.)+)/i);
                            }

                            if (previewMatch) {
                              let previewText = previewMatch[1].trim();
                              // Handle escaped quotes and special characters
                              previewText = previewText.replace(/\\"/g, '"')
                                .replace(/\\n/g, '\n')
                                .replace(/\\t/g, '\t')
                                .replace(/\\r/g, '\r');

                              // Check if preview continues on next line (multi-line preview)
                              if (j + 1 < allLines.length && !allLines[j + 1].trim().match(/^[-*]\s*\*\*/) &&
                                !allLines[j + 1].trim().match(/^###\s+\[\d+\]/)) {
                                const nextLineContent = allLines[j + 1].replace(/<[^>]*>/g, '').trim();
                                // If next line doesn't look like a new metadata field, append it
                                if (nextLineContent && !nextLineContent.match(/^[-*]\s*(Page|Confidence|Preview)/i)) {
                                  previewText += ' ' + nextLineContent.replace(/^"|"$|\.\.\.$/g, '');
                                }
                              }

                              // Remove trailing ellipsis if present
                              previewText = previewText.replace(/\.\.\.$/, '').trim();

                              if (previewText.length > 0) {
                                citation.quote = previewText;
                                citation.text = previewText;

                                // Debug logging (only in development)
                                if (process.env.NODE_ENV === 'development') {
                                  console.log(`[ReportViewer] Extracted preview for citation ${citationNum}:`, {
                                    length: previewText.length,
                                    preview: previewText.substring(0, 100) + '...'
                                  });
                                }
                              }
                            }

                            // Also check for content without quotes (fallback)
                            if (!citation.quote) {
                              const contentMatch = nextLine.match(/^[-*]\s*\*\*Preview:\*\*\s*(.+?)(?:\.\.\.)?$/i) ||
                                nextLine.match(/^[-*]\s*Preview[:\s]+(.+?)(?:\.\.\.)?$/i);
                              if (contentMatch && contentMatch[1].trim().length > 10) {
                                citation.quote = contentMatch[1].trim().replace(/\.\.\.$/, '');
                                citation.text = citation.quote;

                                if (process.env.NODE_ENV === 'development') {
                                  console.log(`[ReportViewer] Extracted preview (no quotes) for citation ${citationNum}`);
                                }
                              }
                            }
                          }
                        }

                        // Only add if we have a valid source filename
                        if (source && source.length > 0 && !source.match(/^[#\[\]()]/)) {
                          // Log if we're missing content (for debugging)
                          if (process.env.NODE_ENV === 'development' && !citation.quote && !citation.text) {
                            console.warn(`[ReportViewer] Citation ${citationNum} has no preview content:`, {
                              source,
                              page: citation.page,
                              confidence: citation.confidence
                            });
                          }
                          citationsMap.set(citationNum, citation);
                        }
                      }
                    }

                    // Convert map to sorted array
                    citations = Array.from(citationsMap.values())
                      .sort((a, b) => a.index - b.index);
                  }

                  // Strategy 2: If no citations section or empty, try to extract from report sections
                  // This is a fallback - should rarely be needed if Citations section exists
                  if (citations.length === 0) {
                    // Try to find citations embedded in report sections
                    // Look for patterns like "Source: filename.pdf" or references to documents
                    const citationPatterns = [
                      /Source[:\s]+([^\n,]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                      /Document[:\s]+([^\n,]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                      /\[(\d+)\]\s+([^\n]+\.(pdf|txt|xlsx?|docx?|csv|pptx?))/gi,
                    ];

                    const foundCitations = new Map<number, any>();

                    for (const pattern of citationPatterns) {
                      let match;
                      while ((match = pattern.exec(reportV1Content)) !== null) {
                        const num = match[1] ? parseInt(match[1]) : foundCitations.size + 1;
                        const filename = match[2] || match[1] || `Document ${num}`;

                        if (!foundCitations.has(num) && filename && filename.length > 0) {
                          foundCitations.set(num, {
                            index: num,
                            source: filename.trim(),
                            text: '',
                            quote: '',
                            page: null,
                            confidence: undefined,
                            section: 'Research Draft',
                            evidence_id: null,
                            artifact_id: null,
                          });
                        }
                      }
                    }

                    citations = Array.from(foundCitations.values())
                      .sort((a, b) => a.index - b.index);
                  }

                  // If no citations section found, extract inline citations with context
                  if (citations.length === 0) {
                    const citationNumbers = new Set<number>();
                    const inlineCitations = reportV1Content.matchAll(/\[(\d+)\]/g);

                    for (const match of inlineCitations) {
                      citationNumbers.add(parseInt(match[1]));
                    }

                    // Extract citation context from document
                    const lines = reportV1Content.split('\n');
                    const citationMap = new Map<number, { source: string; context: string }>();

                    for (let i = 0; i < lines.length; i++) {
                      const match = lines[i].match(/\[(\d+)\]\s*(.+)/);
                      if (match && citationNumbers.has(parseInt(match[1]))) {
                        const citNum = parseInt(match[1]);
                        const source = match[2].trim();

                        // Get surrounding context (previous line for quote)
                        const context = i > 0 ? lines[i - 1].substring(0, 150) : '';

                        citationMap.set(citNum, { source, context });
                      }
                    }

                    citations = Array.from(citationNumbers).sort((a, b) => a - b).map(num => {
                      const data = citationMap.get(num);
                      return {
                        index: num,
                        source: data?.source || `Citation ${num}`,
                        text: data?.context || '',
                        quote: data?.context || '',
                        section: 'Research Draft',
                        evidence_id: null,
                        artifact_id: null,
                      };
                    });
                  }
                }

                // Build comprehensive citation map from sections - GROUP BY DOCUMENT (artifact_id)
                // This ensures one citation per document, with all chunks collected
                const citationToSectionsMap = new Map<string, Set<string>>(); // citation key -> set of section names
                const allCitationsMap = new Map<string, any>(); // citation key -> citation object

                if (report?.sections && Array.isArray(report.sections)) {
                  report.sections.forEach((section: any) => {
                    if (section.citations && Array.isArray(section.citations)) {
                      section.citations.forEach((citation: any) => {
                        // Group by artifact_id (document) - this is the key change
                        // If no artifact_id, group by source filename
                        const citationKey = citation.artifact_id
                          ? `artifact_${citation.artifact_id}`
                          : citation.source || citation.artifact_filename || 'unknown';

                        // Get document name - prioritize artifact_filename, then source
                        const documentName = citation.artifact_filename || citation.source || 'Unknown Document';

                        if (!allCitationsMap.has(citationKey)) {
                          // First time seeing this document - create citation entry
                          allCitationsMap.set(citationKey, {
                            index: allCitationsMap.size + 1,
                            source: documentName, // Document name
                            artifact_id: citation.artifact_id,
                            // Carry through storage_path if already present on the raw citation
                            // (report_generator embeds it when chunk metadata has the field)
                            storage_path: citation.storage_path || null,
                            // Collect all chunks/quotes for this document
                            chunks: citation.chunk_id ? [{
                              chunk_id: citation.chunk_id,
                              text: citation.text || citation.content || '',
                              quote: citation.text || citation.content || '',
                              page: citation.page || citation.page_number,
                              confidence: citation.confidence,
                              evidence_id: citation.evidence_id,
                            }] : [],
                            // For display, use first chunk's content as primary quote
                            text: citation.text || citation.content || '',
                            quote: citation.text || citation.content || '',
                            // Collect all pages
                            pages: citation.page || citation.page_number ? [citation.page || citation.page_number] : [],
                            page: citation.page || citation.page_number, // Primary page (first one)
                            // Sections using this citation
                            sections: [section.section_name],
                            section: section.section_name,
                            // Confidence - average or use first one
                            confidence: citation.confidence,
                            confidences: citation.confidence ? [citation.confidence] : [],
                            // Evidence and chunk IDs
                            evidence_id: citation.evidence_id,
                            chunk_id: citation.chunk_id, // Primary chunk_id (first one)
                            chunk_ids: citation.chunk_id ? [citation.chunk_id] : [],
                            // Section IDs
                            section_id: section.id,
                            section_ids: [section.id],
                          });
                          citationToSectionsMap.set(citationKey, new Set([section.section_name]));
                        } else {
                          // Document already exists - add this chunk to it
                          const existing = allCitationsMap.get(citationKey);

                          // Add section if not already present
                          if (!existing.sections.includes(section.section_name)) {
                            existing.sections.push(section.section_name);
                          }
                          if (!existing.section_ids.includes(section.id)) {
                            existing.section_ids.push(section.id);
                          }
                          citationToSectionsMap.get(citationKey)?.add(section.section_name);

                          // Add chunk if it's a new chunk
                          if (citation.chunk_id && !existing.chunk_ids.includes(citation.chunk_id)) {
                            existing.chunk_ids.push(citation.chunk_id);
                            existing.chunks.push({
                              chunk_id: citation.chunk_id,
                              text: citation.text || citation.content || '',
                              quote: citation.text || citation.content || '',
                              page: citation.page || citation.page_number,
                              confidence: citation.confidence,
                              evidence_id: citation.evidence_id,
                            });
                          }

                          // Add page if new
                          const page = citation.page || citation.page_number;
                          if (page && !existing.pages.includes(page)) {
                            existing.pages.push(page);
                          }

                          // Add confidence if available
                          if (citation.confidence && !existing.confidences.includes(citation.confidence)) {
                            existing.confidences.push(citation.confidence);
                            // Update average confidence
                            const avgConf = existing.confidences.reduce((a: number, b: number) => a + b, 0) / existing.confidences.length;
                            existing.confidence = avgConf;
                          }

                          // Update primary quote if this one is longer/more informative
                          const newText = citation.text || citation.content || '';
                          if (newText && newText.length > (existing.quote?.length || 0)) {
                            existing.quote = newText;
                            existing.text = newText;
                          }
                        }
                      });
                    }
                  });

                  // Prefer v1 citations (full RAG list) as the primary source.
                  // Section citations typically cover only the v2 structured sections and have far fewer entries.
                  // If v1 already found citations, enrich them with section metadata (section_ids for "remove"
                  // to work) rather than replacing. Only fall back to section citations when v1 is empty.
                  if (allCitationsMap.size > 0) {
                    if (citations.length > 0) {
                      // Enrich each v1 citation with section metadata from the sections map (if matched by artifact_id)
                      citations = citations.map((c: any) => {
                        const key = c.artifact_id ? `artifact_${c.artifact_id}` : c.source;
                        const sectionData = allCitationsMap.get(key);
                        if (sectionData) {
                          return {
                            ...c,
                            sections: sectionData.sections,
                            section: sectionData.section,
                            section_id: sectionData.section_id,
                            section_ids: sectionData.section_ids,
                            storage_path: c.storage_path || sectionData.storage_path || null,
                            // Keep v1 chunk_id/confidence; only add section_ids from sections map
                          };
                        }
                        return c;
                      });
                      // Also add any section citations whose artifact is NOT represented in the v1 list
                      // (edge case: v2-only sections that reference a document the v1 draft didn't cite)
                      allCitationsMap.forEach((sectionCit: any, key: string) => {
                        const alreadyPresent = citations.some((c: any) => {
                          const cKey = c.artifact_id ? `artifact_${c.artifact_id}` : c.source;
                          return cKey === key;
                        });
                        if (!alreadyPresent) {
                          citations.push(sectionCit);
                        }
                      });
                    } else {
                      // v1 had no citations — fall back to section citations
                      citations = Array.from(allCitationsMap.values());
                    }
                  }
                }

                // Show .pdf and .docx only when the file exists in the project artifacts OR when the
                // citation already has a valid artifact_id (RAG drive-sync docs have artifact_ids but
                // are not project-scoped, so they would otherwise be filtered out incorrectly).
                const isPdfOrDocx = (name: string) => {
                  const n = (name || '').trim().toLowerCase();
                  return n.endsWith('.pdf') || n.endsWith('.docx');
                };
                // Returns true when the storage_path indicates an uploaded RFP requirement doc
                // (i.e. stored in the artifacts/ bucket — not a real project deliverable).
                // Normalise to forward slashes so Windows (\artifacts\) and Unix (/artifacts/) paths
                // both match the same single check.
                const isUploadedRfpDoc = (c: any): boolean => {
                  const rawPath = (
                    c.storage_path ||
                    (c.artifact_id && citationArtifactMap[c.artifact_id]?.storage_path) ||
                    ''
                  );
                  if (!rawPath) return false;
                  const normalised = rawPath.replace(/\\/g, '/').toLowerCase();
                  return normalised.includes('/artifacts/') || normalised.startsWith('artifacts/');
                };
                citations = citations.filter((c) => {
                  // Hide citations with no artifact_id — they cannot be traced to a real document.
                  if (!c.artifact_id) return false;
                  // Always hide citations from the uploaded-RFP-doc bucket — these are requirement
                  // forms / bid docs, not evidence of past work Keystone has actually done.
                  if (isUploadedRfpDoc(c)) return false;
                  const name = (c.source || c.artifact_filename || '').trim();
                  if (!isPdfOrDocx(name)) return true;
                  // artifact_id present → document exists in the DB
                  return true;
                });
                // Re-index after filtering so displayed indices are 1, 2, 3...
                // Populate the ref so the scroll effect can remap LLM source numbers.
                const origToNew = new Map<number, number>();
                citations.forEach((c, i) => {
                  origToNew.set(c.index, i + 1);
                  c.index = i + 1;
                });
                citationNumToTabIndexRef.current = origToNew;
                // Collect all artifact_ids from v1 citations so the enrichment effect can
                // fetch storage_paths even for docs not referenced in report.sections.
                const newV1Ids = citations
                  .filter((c: any) => c.artifact_id)
                  .map((c: any) => c.artifact_id as string);
                if (
                  newV1Ids.length !== v1CitationArtifactIdsRef.current.length ||
                  newV1Ids.some((id, i) => id !== v1CitationArtifactIdsRef.current[i])
                ) {
                  v1CitationArtifactIdsRef.current = newV1Ids;
                }

                if (citations.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-theme-border">
                      <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p className="text-gray-600 font-medium mb-2">No citations available</p>
                      <p className="text-sm text-gray-500">
                        {!reportV1Content ? 'Loading v1 report content for citations...' : 'This report does not contain any citations'}
                      </p>
                      {!reportV1Content && (
                        <button
                          onClick={() => {
                            downloadReportV1(reportId)
                              .then(content => setReportV1Content(content))
                              .catch(err => console.error('Failed to load v1:', err));
                          }}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Load Citations from V1
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {/* Numbering mismatch notice — shown when citations come from v2 API sections
                        (each section was generated with its own per-call LLM numbering) */}
                    {report?.sections && Array.isArray(report.sections) && report.sections.length > 0 && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 flex items-start gap-2">
                        <span className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0 text-base">ℹ</span>
                        <p className="text-xs text-amber-800 dark:text-amber-300">
                          <strong>Source numbers may not match.</strong> Each section of the proposal is written by the AI using its own local numbering (e.g. [Source 28] may reference a document that appears as #3 in this list). This tab shows all unique documents de-duplicated across every section — use the document names to cross-reference.
                        </p>
                      </div>
                    )}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-blue-900 dark:text-blue-300 font-medium">
                            <strong>{citations.length}</strong> citation{citations.length !== 1 ? 's' : ''} found in this report
                          </p>
                          {removedArtifactIds.size > 0 ? (
                            <p className="text-xs text-orange-700 dark:text-orange-400 mt-1 font-medium">
                              ⚠️ {removedArtifactIds.size} source{removedArtifactIds.size !== 1 ? 's' : ''} marked for exclusion.
                              These will be excluded when you regenerate sections - they won't be used again.
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {canEditProposal
                                ? <>💡 In the report content, citations like <strong>[1]</strong>, <strong>[Source 28]</strong>, or <strong>[Sources 1, 12, 27]</strong> are clickable and jump to this Evidence Trail. Click the trash icon on any citation to remove it, then use Regenerate Sections to update the report.</>
                                : <>💡 In the report content, citations like <strong>[1]</strong> are clickable and jump to this Evidence Trail (read-only).</>}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {citations.some(c => normalizeConfidenceForDisplay(c.confidence) !== null) && (
                            <div className="text-xs text-blue-700 dark:text-blue-400 whitespace-nowrap">
                              {citations.filter(c => { const pct = normalizeConfidenceForDisplay(c.confidence); return pct !== null && pct >= 70; }).length} high confidence
                            </div>
                          )}
                          {canEditProposal && (
                            <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // If report has sections, open modal with section selector
                              if (report?.sections && report.sections.length > 0) {
                                setShowAddCitationModal(true);
                              } else {
                                alert('No sections available. Please ensure the report has sections before adding citations.');
                              }
                            }}
                            className="flex items-center gap-2"
                            title="Add new citations to a report section"
                          >
                            <Plus className="h-4 w-4" />
                            Add Citation
                          </Button>
                          {removedArtifactIds.size > 0 && (
                            <Button
                              size="sm"
                              onClick={() => handleRegenerateSections()}
                              disabled={isRegenerating}
                              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                              {isRegenerating ? 'Regenerating...' : `Regenerate ${report?.sections?.length || 0} Sections`}
                            </Button>
                          )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Bulk Operations - Select multiple citations */}
                    {citations.length > 1 && canEditProposal && (
                      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {!isBulkMode ? (
                              <>
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Select multiple citations to remove in bulk
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setIsBulkMode(true)}
                                  className="text-xs"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Select Multiple
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {selectedCitationIds.size} citation{selectedCitationIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedCitationIds(new Set());
                                    setIsBulkMode(false);
                                  }}
                                  className="text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={async () => {
                                    if (selectedCitationIds.size === 0) {
                                      alert('Please select at least one citation to remove');
                                      return;
                                    }

                                    const selectedCitations = citations.filter(c => {
                                      const citationKey = c.artifact_id || c.source || `citation-${c.index}`;
                                      return selectedCitationIds.has(citationKey);
                                    });

                                    const affectedSections = new Set<string>();
                                    const affectedSectionIds = new Set<string>();
                                    selectedCitations.forEach(c => {
                                      (c.sections || []).forEach((s: string) => affectedSections.add(s));
                                      (c.section_ids || []).forEach((id: string) => affectedSectionIds.add(id));
                                    });

                                    const confirmMessage = `Remove ${selectedCitationIds.size} citation(s) from the report?\n\n` +
                                      `Affected Sections: ${Array.from(affectedSections).join(', ')}\n` +
                                      `This will remove the citations and you can regenerate ${affectedSectionIds.size} section(s) without these sources.`;

                                    if (!confirm(confirmMessage)) {
                                      return;
                                    }

                                    try {
                                      // Remove each citation
                                      for (const citation of selectedCitations) {
                                        if (citation.artifact_id) {
                                          await removeCitation(reportId, {
                                            artifact_id: citation.artifact_id
                                          });
                                          setRemovedArtifactIds(prev => new Set([...prev, citation.artifact_id!]));
                                        }
                                      }

                                      // Clear selection
                                      setSelectedCitationIds(new Set());
                                      setIsBulkMode(false);

                                      // Refresh report content
                                      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
                                      downloadReportV1(reportId)
                                        .then(content => setReportV1Content(content))
                                        .catch(err => console.error('Failed to reload v1:', err));
                                      downloadReportV2(reportId)
                                        .then(content => setReportContent(content))
                                        .catch(err => console.error('Failed to reload v2:', err));

                                      alert(`Successfully removed ${selectedCitationIds.size} citation(s). ${affectedSectionIds.size} section(s) affected. Use "Regenerate Sections" to update the report content.`);
                                    } catch (error: any) {
                                      console.error('Failed to remove citations:', error);
                                      alert(`Failed to remove citations: ${error.message || 'Unknown error'}`);
                                    }
                                  }}
                                  disabled={selectedCitationIds.size === 0}
                                  className="text-xs bg-red-600 hover:bg-red-700 text-white"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Remove {selectedCitationIds.size} Selected
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {[...citations]
                      .sort((a, b) => {
                        const isUnknownOrNoArtifact = (c: typeof a) =>
                          !c.artifact_id && (!c.source || String(c.source).trim().toLowerCase() === 'unknown');
                        const aTail = isUnknownOrNoArtifact(a);
                        const bTail = isUnknownOrNoArtifact(b);
                        if (aTail === bTail) return a.index - b.index;
                        return aTail ? 1 : -1;
                      })
                      .map((citation) => {
                        // Get affected section IDs for this citation
                        const affectedSectionIds = citation.section_ids ||
                          (report?.sections
                            ?.filter((s: any) => (citation.sections || [citation.section]).includes(s.section_name))
                            .map((s: any) => s.id) || []);

                        const citationKey = citation.artifact_id || citation.source || `citation-${citation.index}`;
                        const isSelected = selectedCitationIds.has(citationKey);

                        return (
                          <div
                            id={`citation-${citation.index}`}
                            key={citation.index}
                            className={`bg-white dark:bg-theme-paper border-2 rounded-lg p-4 transition-all shadow-sm hover:shadow-md ${isBulkMode
                              ? isSelected
                                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                              } ${isBulkMode ? 'cursor-pointer' : ''}`}
                            onClick={isBulkMode ? () => {
                              setSelectedCitationIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(citationKey)) {
                                  newSet.delete(citationKey);
                                } else {
                                  newSet.add(citationKey);
                                }
                                return newSet;
                              });
                            } : undefined}
                          >
                            <div className="flex items-start gap-3">
                              {/* Selection Checkbox (only in bulk mode) */}
                              {isBulkMode && (
                                <div className="flex-shrink-0 mt-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setSelectedCitationIds(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(citationKey)) {
                                          newSet.delete(citationKey);
                                        } else {
                                          newSet.add(citationKey);
                                        }
                                        return newSet;
                                      });
                                    }}
                                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              )}
                              <div className="bg-blue-600 dark:bg-blue-500 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold flex-shrink-0 text-sm shadow-sm">
                                {citation.index}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between mb-2 gap-2">
                                  <div className="font-semibold text-theme-text flex items-center gap-2 flex-1 min-w-0">
                                    <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                    <span className="truncate" title={citation.source}>
                                      {citation.source || 'Unknown Source'}
                                    </span>
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0 items-center">
                                    {citation.artifact_id ? (
                                      <>
                                        {removedArtifactIds.has(citation.artifact_id) ? (
                                          <>
                                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium whitespace-nowrap">
                                              Removed
                                            </span>
                                            {affectedSectionIds.length > 0 && !isBulkMode && canEditProposal && (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleRegenerateSections(affectedSectionIds)}
                                                disabled={isRegenerating}
                                                className="text-xs bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                                                title={`Regenerate ${affectedSectionIds.length} affected section(s) without this source`}
                                              >
                                                <RefreshCw className={`h-3 w-3 ${isRegenerating ? 'animate-spin' : ''}`} />
                                                Regenerate
                                              </Button>
                                            )}
                                          </>
                                        ) : (
                                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium whitespace-nowrap">
                                            Available
                                          </span>
                                        )}
                                        {!isBulkMode && canEditProposal && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveCitation(citation)}
                                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                                            title={`Remove this citation from ${citation.sections?.length || 1} section(s) and regenerate without it`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      <span className="px-2 py-1 bg-theme-surface-hover text-gray-600 dark:text-gray-400 rounded text-xs font-medium whitespace-nowrap" title="This citation does not have an associated artifact ID">
                                        No artifact ID
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Artifact ID - visible and copyable */}
                                {citation.artifact_id && (
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Artifact ID:</span>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(citation.artifact_id!);
                                          addToast({ type: 'success', message: 'Artifact ID copied to clipboard.' });
                                        } catch (err) {
                                          console.error('Failed to copy:', err);
                                        }
                                      }}
                                      className="font-mono text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded border border-purple-200 dark:border-purple-800 break-all text-left hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                                      title="Click to copy full Artifact ID"
                                    >
                                      {citation.artifact_id}
                                    </button>
                                  </div>
                                )}

                                {/* Source file path (from citation data or enriched from artifact API) */}
                                {(citation.storage_path || citation.meta?.relative_path || citation.meta?.original_path || (citation.artifact_id && citationArtifactMap[citation.artifact_id]?.storage_path)) && (() => {
                                  const rawPath = citation.storage_path || citation.meta?.relative_path || citation.meta?.original_path || citationArtifactMap[citation.artifact_id]?.storage_path || '';
                                  const displayPath = rawPath.replace(/^unc:\/\//i, '\\\\').replace(/^\/\//, '\\\\').replace(/\//g, '\\');
                                  return (
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Source path:</span>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            await navigator.clipboard.writeText(rawPath);
                                            addToast({ type: 'success', message: 'Source path copied to clipboard.' });
                                          } catch (err) {
                                            console.error('Failed to copy path:', err);
                                          }
                                        }}
                                        className="font-mono text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded border border-blue-200 dark:border-blue-800 break-all text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                        title="Click to copy source path"
                                      >
                                        {displayPath}
                                      </button>
                                    </div>
                                  );
                                })()}

                                {/* Quote/Excerpt - Show chunks with markdown formatting */}
                                {citation.chunks && citation.chunks.length > 0 ? (
                                  <div className="space-y-2 mb-2">
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                      {citation.chunks.length} chunk{citation.chunks.length !== 1 ? 's' : ''} from this document:
                                    </div>
                                    {citation.chunks.slice(0, 5).map((chunk: any, idx: number) => {
                                      const chunkContent = chunk.quote || chunk.text || '';
                                      const hasContent = chunkContent && String(chunkContent).trim() && String(chunkContent) !== citation.source;
                                      return (
                                        <div key={idx} className="bg-gray-50 dark:bg-gray-800 border-l-4 border-blue-400 dark:border-blue-500 pl-3 py-2 rounded-r">
                                          <div className="flex items-start gap-2">
                                            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                              Chunk {idx + 1}:
                                            </span>
                                            <div className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                                              {hasContent ? (
                                                <CollapsibleChunkContent lines={3}>
                                                  <div
                                                    dangerouslySetInnerHTML={{
                                                      __html: convertMarkdownToHTML(String(chunkContent).replace(/^["']|["']$/g, '').trim())
                                                    }}
                                                  />
                                                </CollapsibleChunkContent>
                                              ) : (
                                                <span className="text-gray-500 dark:text-gray-400 italic">No content</span>
                                              )}
                                            </div>
                                          </div>
                                          {(chunk.page || chunk.confidence !== undefined) && (
                                            <div className="flex flex-wrap gap-2 mt-1 ml-12 text-xs text-gray-500 dark:text-gray-400">
                                              {chunk.page && <span>Page {chunk.page}</span>}
                                              {(() => {
                                                const pct = normalizeConfidenceForDisplay(chunk.confidence);
                                                return pct !== null && pct >= 15 ? (
                                                  <span>{pct}% confidence</span>
                                                ) : null;
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {citation.chunks.length > 5 && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                        + {citation.chunks.length - 5} more chunk{citation.chunks.length - 5 !== 1 ? 's' : ''}
                                      </p>
                                    )}
                                  </div>
                                ) : citation.quote && citation.quote.trim() && citation.quote !== citation.source && !citation.quote.match(/^[#\[\]()]/) ? (
                                  <div className="bg-gray-50 dark:bg-gray-800 border-l-4 border-blue-400 dark:border-blue-500 pl-3 py-2 mb-2 rounded-r">
                                    <div className="flex items-start gap-2">
                                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                        Excerpt:
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <CollapsibleChunkContent lines={3}>
                                          <div
                                            className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1"
                                            dangerouslySetInnerHTML={{
                                              __html: convertMarkdownToHTML(String(citation.quote).replace(/^["']|["']$/g, '').trim())
                                            }}
                                          />
                                        </CollapsibleChunkContent>
                                      </div>
                                    </div>
                                  </div>
                                ) : citation.text && citation.text.trim() && citation.text !== citation.source && !citation.text.match(/^[#\[\]()]/) ? (
                                  <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 mb-2 border border-gray-200 dark:border-gray-700">
                                    <div className="flex items-start gap-2">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex-shrink-0 mt-0.5">
                                        Content:
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <CollapsibleChunkContent lines={3}>
                                          <div
                                            className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1"
                                            dangerouslySetInnerHTML={{
                                              __html: convertMarkdownToHTML(String(citation.text).trim())
                                            }}
                                          />
                                        </CollapsibleChunkContent>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-300 dark:border-blue-600 pl-3 py-2 mb-2">
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                      <span className="font-medium">Document:</span> {citation.source}
                                      {citation.pages && citation.pages.length > 0 && (
                                        <span className="ml-2 text-blue-600">
                                          (Pages {citation.pages.sort((a: number, b: number) => a - b).join(', ')})
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                )}

                                {/* Metadata Row - Confidence, Page, Artifact ID, Chunk ID */}
                                <div className="flex flex-wrap items-center gap-2 text-xs mt-2">
                                  {/* Confidence - normalized so 0–1, 0–100, or wrong scale (e.g. 28689) display correctly */}
                                  {(() => {
                                    const pct = normalizeConfidenceForDisplay(citation.confidence);
                                    if (pct === null || pct < 15) return null;
                                    return (
                                      <span className={`px-3 py-1.5 rounded-lg font-semibold border-2 ${pct >= 70
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
                                        : pct >= 50
                                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700'
                                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
                                        }`}>
                                        {pct >= 70 ? '✓' : pct >= 50 ? '⚠' : '✗'} {pct}% Confidence
                                      </span>
                                    );
                                  })()}

                                  {/* Pages - Show all pages from this document */}
                                  {citation.pages && citation.pages.length > 0 ? (
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-600">
                                      📄 Pages {citation.pages.sort((a: number, b: number) => a - b).join(', ')}
                                    </span>
                                  ) : citation.page ? (
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-gray-600">
                                      📄 Page {citation.page}
                                    </span>
                                  ) : null}

                                  {/* Chunk IDs - Show all if multiple, or single if one */}
                                  {citation.chunk_ids && citation.chunk_ids.length > 0 && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const chunkIdsText = citation.chunk_ids.join(', ');
                                          await navigator.clipboard.writeText(chunkIdsText);
                                          alert(`Copied ${citation.chunk_ids.length} chunk ID(s) to clipboard!`);
                                        } catch (err) {
                                          console.error('Failed to copy:', err);
                                        }
                                      }}
                                      className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors flex items-center gap-1 border border-indigo-200 dark:border-indigo-800 font-mono text-xs"
                                      title={`Click to copy ${citation.chunk_ids.length} chunk ID(s)`}
                                    >
                                      <Code className="h-3 w-3" />
                                      Copy chunk IDs
                                    </button>
                                  )}

                                  {/* Section(s) - Show all sections using this citation */}
                                  {citation.sections && Array.isArray(citation.sections) ? (
                                    <div className="flex flex-wrap gap-1">
                                      {citation.sections.map((sectionName: string, idx: number) => (
                                        <span
                                          key={idx}
                                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium border border-blue-200 dark:border-blue-800 max-w-[200px] truncate inline-block"
                                          title={`Used in section: ${sectionName}`}
                                        >
                                          {truncateToWords(sectionName, 6)}
                                        </span>
                                      ))}
                                      {citation.sections.length > 1 && (
                                        <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded text-xs">
                                          +{citation.sections.length - 1} more
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium border border-blue-200 dark:border-blue-800 max-w-[200px] truncate inline-block" title={citation.section || 'Research Draft'}>
                                      {truncateToWords(citation.section || 'Research Draft', 6)}
                                    </span>
                                  )}

                                  {/* Evidence ID - Trace button */}
                                  {citation.evidence_id && (
                                    <button
                                      onClick={() => handleTraceEvidence(citation.evidence_id!)}
                                      className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors flex items-center gap-1 border border-indigo-200 dark:border-indigo-800"
                                      title="Trace to original evidence and view chunk details"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      Trace Evidence
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}

        {activeTab === 'evidencegaps' && (
          <Card title="Evidence Gaps & Recommendations">
            <div className="space-y-6">
              {(() => {
                type GapNoEvidence = { question: string; need: string; confidence: string; impactedSection?: string };
                type GapLowConfidence = { question: string; confidence: string; recommendation: string; impactedSection?: string };
                type GapIncomplete = { question: string; confidence: string; enhancement: string; impactedSection?: string };
                let totalGaps = 0;
                const noEvidence: GapNoEvidence[] = [];
                const lowConfidence: GapLowConfidence[] = [];
                const incomplete: GapIncomplete[] = [];
                let questionToSection: Record<string, string> = {};

                if (reportV1Content) {
                  // Build question -> section name map from v1 body (before Evidence Gaps section)
                  const bodyBeforeGaps = reportV1Content.split(/## Evidence Gaps & Recommendations/i)[0] || '';
                  const sectionBlocks = bodyBeforeGaps.split(/\n##\s+/).filter(Boolean);
                  for (let i = 1; i < sectionBlocks.length; i++) {
                    const block = sectionBlocks[i];
                    const firstLineEnd = block.indexOf('\n');
                    const sectionName = firstLineEnd >= 0 ? block.slice(0, firstLineEnd).trim() : block.trim();
                    const rest = firstLineEnd >= 0 ? block.slice(firstLineEnd + 1) : '';
                    const questionParts = rest.split(/\n###\s+/).filter(Boolean);
                    for (const part of questionParts) {
                      const qFirstLine = part.split('\n')[0].trim();
                      if (qFirstLine.length > 20) {
                        const key = qFirstLine.slice(0, 180).trim();
                        questionToSection[key] = sectionName;
                      }
                    }
                  }
                  const inferSection = (question: string): string | undefined => {
                    const firstLine = question.split('\n')[0].trim().slice(0, 180);
                    if (questionToSection[firstLine]) return questionToSection[firstLine];
                    for (const [key, name] of Object.entries(questionToSection)) {
                      if (question.includes(key.slice(0, 80)) || key.includes(question.slice(0, 80))) return name;
                    }
                    return undefined;
                  };

                  const sectionMatch = reportV1Content.match(/## Evidence Gaps & Recommendations\s*([\s\S]*?)(?=\n##\s|$)/i);
                  const section = sectionMatch ? sectionMatch[1] : '';
                  const totalMatch = section.match(/\*\*Total Gaps Identified:\*\*\s*(\d+)/i);
                  totalGaps = totalMatch ? parseInt(totalMatch[1], 10) : 0;

                  const questionFromBlock = (block: string): string => {
                    const lines = block.trim().split('\n');
                    let end = lines.length;
                    for (let i = 0; i < lines.length; i++) {
                      if (lines[i].match(/^[-*]\s*\*\*/)) { end = i; break; }
                    }
                    return lines.slice(0, end).join('\n').trim();
                  };

                  const untilMediumOrRule = '(?=\\n---|###\\s*(?:🟡\\s*)?Medium:\\s*Low Confidence|###\\s*(?:🟢\\s*)?Low Priority:\\s*Incomplete|$)';
                  const untilLowPriorityOrRule = '(?=\\n---|###\\s*(?:🟢\\s*)?Low Priority:\\s*Incomplete|$)';

                  const criticalBlock = section.match(new RegExp('###\\s*(?:🔴\\s*)?Critical:\\s*No Evidence Found\\s*([\\s\\S]*?)' + untilMediumOrRule, 'i'))?.[1] || '';
                  criticalBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
                    const needMatch = block.match(/[-*]\s*\*\*Need:\*\*\s*([^\n]+)/);
                    const confMatch = block.match(/[-*]\s*\*\*Current Confidence:\*\*\s*([\d.]+)%?/);
                    const question = questionFromBlock(block);
                    if (question && needMatch) {
                      noEvidence.push({
                        question,
                        need: needMatch[1].trim(),
                        confidence: confMatch ? `${confMatch[1]}%` : '0%',
                        impactedSection: inferSection(question),
                      });
                    }
                  });

                  const mediumBlock = section.match(new RegExp('###\\s*(?:🟡\\s*)?Medium:\\s*Low Confidence Answers\\s*([\\s\\S]*?)' + untilLowPriorityOrRule, 'i'))?.[1] || '';
                  mediumBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
                    const confMatch = block.match(/[-*]\s*\*\*Current Confidence:\*\*\s*([\d.]+)%?/);
                    const recMatch = block.match(/[-*]\s*\*\*Recommendation:\*\*\s*([^\n]+)/);
                    const question = questionFromBlock(block);
                    if (question && recMatch) {
                      lowConfidence.push({
                        question,
                        confidence: confMatch ? `${confMatch[1]}%` : '—',
                        recommendation: recMatch[1].trim(),
                        impactedSection: inferSection(question),
                      });
                    }
                  });

                  const incompleteBlock = section.match(/###\s*(?:🟢\s*)?Low Priority:\s*Incomplete Coverage\s*([\s\S]*?)(?=\n---|$)/i)?.[1] || '';
                  incompleteBlock.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
                    const confMatch = block.match(/[-*]\s*\*\*Current Confidence:\*\*\s*([\d.]+)%?/);
                    const enhMatch = block.match(/[-*]\s*\*\*Enhancement:\*\*\s*([^\n]+)/);
                    const question = questionFromBlock(block);
                    if (question && (confMatch || enhMatch)) {
                      incomplete.push({
                        question,
                        confidence: confMatch ? `${confMatch[1]}%` : '—',
                        enhancement: enhMatch ? enhMatch[1].trim() : '',
                        impactedSection: inferSection(question),
                      });
                    }
                  });

                  if (totalGaps > 0 && noEvidence.length === 0 && lowConfidence.length === 0 && incomplete.length === 0) {
                    section.split(/\*\*Q:\*\*\s*/).filter(Boolean).forEach((block) => {
                      const needMatch = block.match(/[-*]\s*\*\*Need:\*\*\s*([^\n]+)/);
                      const recMatch = block.match(/[-*]\s*\*\*Recommendation:\*\*\s*([^\n]+)/);
                      const enhMatch = block.match(/[-*]\s*\*\*Enhancement:\*\*\s*([^\n]+)/);
                      const confMatch = block.match(/[-*]\s*\*\*Current Confidence:\*\*\s*([\d.]+)%?/);
                      const question = questionFromBlock(block);
                      if (!question) return;
                      if (needMatch) noEvidence.push({ question, need: needMatch[1].trim(), confidence: confMatch ? `${confMatch[1]}%` : '0%', impactedSection: inferSection(question) });
                      else if (recMatch) lowConfidence.push({ question, confidence: confMatch ? `${confMatch[1]}%` : '—', recommendation: recMatch[1].trim(), impactedSection: inferSection(question) });
                      else if (enhMatch || confMatch) incomplete.push({ question, confidence: confMatch ? `${confMatch[1]}%` : '—', enhancement: enhMatch ? enhMatch[1].trim() : '', impactedSection: inferSection(question) });
                    });
                  }
                }

                const hasAny = noEvidence.length > 0 || lowConfidence.length > 0 || incomplete.length > 0;
                const displayTotal = totalGaps > 0 ? totalGaps : (noEvidence.length + lowConfidence.length + incomplete.length);

                const openAddCitationForSection = (sectionName: string | undefined) => {
                  if (!report?.sections?.length) {
                    alert('No sections available. Ensure the report has sections before adding citations.');
                    return;
                  }
                  const sec = report.sections.find((s: any) => (s.section_name || (s as any).name || '').trim() === (sectionName || '').trim());
                  if (sec) {
                    setAddCitationSectionId(sec.id);
                    setAddCitationSectionName(sec.section_name || (sec as any).name);
                    setShowAddCitationModal(true);
                  } else {
                    setAddCitationSectionId(undefined);
                    setAddCitationSectionName(undefined);
                    setShowAddCitationModal(true);
                  }
                };

                const groupBy = <T,>(items: T[], keyFn: (t: T) => string): Array<{ key: string; items: T[] }> => {
                  const map = new Map<string, T[]>();
                  items.forEach((item) => {
                    const k = keyFn(item);
                    if (!map.has(k)) map.set(k, []);
                    map.get(k)!.push(item);
                  });
                  return Array.from(map.entries()).map(([key, items]) => ({ key, items }));
                };

                if (!reportV1Content) {
                  return (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-theme-border">
                      <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-orange-400" />
                      <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">Loading evidence gaps</p>
                      <button type="button" onClick={() => downloadReportV1(reportId).then(c => setReportV1Content(c)).catch(console.error)} className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">Load V1 to show gaps</button>
                    </div>
                  );
                }

                if (!hasAny && displayTotal === 0) {
                  return (
                    <div className="text-center py-12 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-dashed border-green-300 dark:border-green-700">
                      <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
                      <p className="text-theme-text font-medium">No evidence gaps identified</p>
                    </div>
                  );
                }

                const noEvGroups = groupBy(noEvidence, g => g.need.slice(0, 120));
                const lowConfGroups = groupBy(lowConfidence, g => g.recommendation.slice(0, 120));
                const incGroups = groupBy(incomplete, g => (g.enhancement || '').slice(0, 80));
                const recommendationTypeCount = noEvGroups.length + lowConfGroups.length + incGroups.length;

                return (
                  <>
                    <div className="flex items-center justify-between gap-4 flex-wrap border-b border-gray-200 dark:border-gray-700 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/30">
                          <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-theme-text">Evidence Gaps & Recommendations</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            <strong>{recommendationTypeCount}</strong> recommendation type{recommendationTypeCount !== 1 ? 's' : ''} to address
                            {displayTotal > 0 && displayTotal !== recommendationTypeCount && (
                              <span> — across {displayTotal} question{displayTotal !== 1 ? 's' : ''}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {noEvGroups.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="flex items-center gap-2 text-base font-semibold text-red-700 dark:text-red-400"><span aria-hidden>🔴</span> Critical: No Evidence Found</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">These questions could not be answered due to missing documents.</p>
                        {noEvGroups.map((group, gidx) => (
                          <div key={`no-ev-g-${gidx}`} className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <span className="text-sm font-medium text-red-800 dark:text-red-300 rounded-full bg-red-100 dark:bg-red-900/30 px-2.5 py-1">Need: {group.key}{group.key.length >= 120 ? '…' : ''}</span>
                              <Button size="sm" variant="outline" onClick={() => openAddCitationForSection(group.items[0]?.impactedSection)} className="flex items-center gap-1 shrink-0">
                                <Plus className="h-3 w-3" /> Add citation
                              </Button>
                            </div>
                            <ul className="space-y-2 list-none">
                              {group.items.map((gap, idx) => (
                                <li key={`no-ev-${gidx}-${idx}`} className="text-sm text-theme-text border-l-2 border-red-300 dark:border-red-700 pl-3 py-1">
                                  {gap.impactedSection && <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Section: {gap.impactedSection}</span>}
                                  <p className="leading-relaxed mt-0.5">Q: {gap.question.slice(0, 300)}{gap.question.length > 300 ? '…' : ''}</p>
                                  <span className="text-xs text-gray-500">Confidence: {gap.confidence}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {lowConfGroups.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="flex items-center gap-2 text-base font-semibold text-amber-700 dark:text-amber-400"><span aria-hidden>🟡</span> Medium: Low Confidence Answers</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">These questions were answered but with low confidence (&lt;50%).</p>
                        {lowConfGroups.map((group, gidx) => (
                          <div key={`low-g-${gidx}`} className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <span className="text-sm font-medium text-amber-800 dark:text-amber-300 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1">Recommendation: {group.key}{group.key.length >= 120 ? '…' : ''}</span>
                              <Button size="sm" variant="outline" onClick={() => openAddCitationForSection(group.items[0]?.impactedSection)} className="flex items-center gap-1 shrink-0">
                                <Plus className="h-3 w-3" /> Add citation
                              </Button>
                            </div>
                            <ul className="space-y-2 list-none">
                              {group.items.map((gap, idx) => (
                                <li key={`low-${gidx}-${idx}`} className="text-sm text-theme-text border-l-2 border-amber-300 dark:border-amber-700 pl-3 py-1">
                                  {gap.impactedSection && <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Section: {gap.impactedSection}</span>}
                                  <p className="leading-relaxed mt-0.5">Q: {gap.question.slice(0, 300)}{gap.question.length > 300 ? '…' : ''}</p>
                                  <span className="text-xs text-gray-500">Confidence: {gap.confidence}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {incGroups.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="flex items-center gap-2 text-base font-semibold text-emerald-700 dark:text-emerald-400"><span aria-hidden>🟢</span> Low Priority: Incomplete Coverage</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">These questions were partially answered but could be enhanced.</p>
                        {incGroups.map((group, gidx) => (
                          <div key={`inc-g-${gidx}`} className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2.5 py-1">{group.key ? `Enhancement: ${group.key}…` : 'Incomplete'}</span>
                              <Button size="sm" variant="outline" onClick={() => openAddCitationForSection(group.items[0]?.impactedSection)} className="flex items-center gap-1 shrink-0">
                                <Plus className="h-3 w-3" /> Add citation
                              </Button>
                            </div>
                            <ul className="space-y-2 list-none">
                              {group.items.map((gap, idx) => (
                                <li key={`inc-${gidx}-${idx}`} className="text-sm text-theme-text border-l-2 border-emerald-300 dark:border-emerald-700 pl-3 py-1">
                                  {gap.impactedSection && <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Section: {gap.impactedSection}</span>}
                                  <p className="leading-relaxed mt-0.5">Q: {gap.question.slice(0, 300)}{gap.question.length > 300 ? '…' : ''}</p>
                                  <span className="text-xs text-gray-500">Confidence: {gap.confidence}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </Card>
        )}

        {activeTab === 'versions' && (
          <Card title="Version History">
            <div className="space-y-3">
              {versions.map((version) => (
                <div
                  key={version.version}
                  className={`border rounded-lg p-4 ${version.status === 'current'
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 bg-white'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 text-gray-700 rounded-lg px-3 py-1 font-mono text-sm">
                        v{version.version}
                      </div>
                      {version.status === 'current' && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Switch to content tab and select this version
                          setActiveTab('content');
                          setSelectedVersion(`v${version.version}`);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </button>
                      {version.status !== 'current' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Restore version ${version.version}? This will replace the current version.`)) {
                              alert('Version restore is not yet implemented. This would call the restore API endpoint.');
                            }
                          }}
                          className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                        >
                          <GitBranch className="h-4 w-4" />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-sm">
                    <p className="text-theme-text font-medium dark:text-black">{version.changes}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>By: {version.created_by}</span>
                      <span>{new Date(version.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {activeTab === 'collaboration' && (
          <ReportCollaboration
            reportId={reportId}
            currentStatus={report.status}
            onStatusChange={(newStatus) => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
          />
        )}

        {showSharingTab && activeTab === 'sharing' && (
          <ReportSharing reportId={reportId} />
        )}

        {activeTab === 'analytics' && (
          <ReportAnalytics reportId={reportId} />
        )}

        {activeTab === 'actions' && (
          canEditProposal ? (
          <ReportActions
            reportId={reportId}
            evidenceGaps={report?.evidence_gaps}
            totalSections={report?.total_questions}
            onRegenerateComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
              setActiveTab('content');
            }}
          />
          ) : (
            <Card><p className="text-sm text-theme-text-secondary p-4">You do not have permission to run proposal actions.</p></Card>
          )
        )}

        {activeTab === 'wordcount' && (
          <WordCountTabContent reportId={reportId} report={report} enableOptimizer={enableWordCountOptimizer && canEditProposal} />
        )}

        {activeTab === 'qa' && (
          <QATabContent
            reportId={reportId}
            executiveSummaryVersionLabel={
              typeof report?.meta?.current_version === 'number'
                ? `v${report.meta.current_version}`
                : undefined
            }
            isProposalReport={isProposalReport}
          />
        )}

        {activeTab === 'review' && (
          <ReviewDashboard
            reportId={reportId}
            onStatusChange={() => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
            onSwitchToQATab={() => setActiveTab('qa')}
          />
        )}

        {/* Keep mounted so edit modal / pending optimistic sections survive tab switches */}
        <div className={activeTab === 'customsections' ? '' : 'hidden'} aria-hidden={activeTab !== 'customsections'}>
          <CustomSectionsTab
            reportId={reportId}
            report={report}
            onSectionUpdate={() => {
              queryClient.invalidateQueries({ queryKey: ['report', reportId] });
            }}
          />
        </div>

        {activeTab === 'staffassignments' && (
          <StaffAssignmentsTab
            reportId={reportId}
            report={report}
            templateJobId={report?.meta?.job_id ?? (report as any)?.metadata?.job_id}
            readOnly={!canEditProposal}
          />
        )}
      </div>

      {/* Add Citation Modal */}
      <AddCitationModal
        isOpen={showAddCitationModal && canEditProposal}
        onClose={() => {
          setShowAddCitationModal(false);
          setAddCitationSectionId(undefined);
          setAddCitationSectionName(undefined);
        }}
        reportId={reportId}
        report={report}
        sectionId={addCitationSectionId}
        sectionName={addCitationSectionName}
        onSuccess={() => {
          // Reload report content after adding citations
          downloadReportV1(reportId)
            .then(content => setReportV1Content(content))
            .catch(err => console.error('Failed to reload v1:', err));
          downloadReportV2(reportId)
            .then(content => setReportContent(content))
            .catch(err => console.error('Failed to reload v2:', err));
        }}
      />

      {/* Evidence Trace Modal */}
      {selectedCitation && selectedCitation.evidence_id && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCitation(null)}>
          <div className="max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Evidence Provenance Trail
                  </h3>
                  <button
                    onClick={() => setSelectedCitation(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>

                {selectedCitation.trace ? (
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg space-y-4">
                    {/* Evidence ID */}
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Evidence ID</div>
                      <div className="text-theme-text font-mono text-sm bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border">
                        {selectedCitation.evidence_id}
                      </div>
                    </div>

                    {/* Source Document */}
                    {selectedCitation.trace.artifact_filename && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Source Document</div>
                        <div className="text-theme-text bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border">
                          {selectedCitation.trace.artifact_filename}
                        </div>
                      </div>
                    )}

                    {/* Content Preview - Full chunk content */}
                    {(selectedCitation.chunk_content || selectedCitation.trace.content) && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Chunk Content</div>
                        <CollapsibleChunkContent lines={3}>
                          <div className="text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border text-sm whitespace-pre-wrap">
                            {selectedCitation.chunk_content || selectedCitation.trace.content}
                          </div>
                        </CollapsibleChunkContent>
                      </div>
                    )}

                    {/* Confidence Breakdown */}
                    {selectedCitation.trace.evidence && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">Evidence Details</div>
                        <div className="bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border space-y-2 text-sm">
                          {selectedCitation.trace.evidence.evidence_type && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">Type: </span>
                              <span className="text-gray-600 dark:text-gray-400">{selectedCitation.trace.evidence.evidence_type}</span>
                            </div>
                          )}
                          {selectedCitation.trace.evidence.page_number && (
                            <div>
                              <span className="font-medium text-gray-700 dark:text-gray-300">Page: </span>
                              <span className="text-gray-600 dark:text-gray-400">{selectedCitation.trace.evidence.page_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="grid grid-cols-2 gap-3">
                      {selectedCitation.trace.page_number && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Page Number</div>
                          <div className="text-theme-text bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border">
                            Page {selectedCitation.trace.page_number}
                          </div>
                        </div>
                      )}
                      {selectedCitation.trace.chunk_id && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Chunk ID</div>
                          <div className="flex items-center gap-2">
                            <div className="text-gray-900 font-mono text-xs bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border flex-1 truncate">
                              {selectedCitation.trace.chunk_id}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(selectedCitation.trace.chunk_id);
                                  alert('Chunk ID copied!');
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                }
                              }}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              title="Copy Chunk ID"
                            >
                              <FileCode className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Chunk Content - Full content from chunk */}
                      {selectedCitation.chunk_content && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Chunk Content</div>
                          <CollapsibleChunkContent lines={3}>
                            <div className="text-gray-700 bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border text-sm whitespace-pre-wrap">
                              {selectedCitation.chunk_content}
                            </div>
                          </CollapsibleChunkContent>
                        </div>
                      )}

                      {/* Artifact ID */}
                      {selectedCitation.trace.artifact?.id && (
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-1">Artifact ID</div>
                          <div className="flex items-center gap-2">
                            <div className="text-theme-text font-mono text-xs bg-white dark:bg-slate-800 px-3 py-2 rounded border border-theme-border flex-1 truncate">
                              {selectedCitation.trace.artifact.id}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(selectedCitation.trace.artifact.id);
                                  alert('Artifact ID copied!');
                                } catch (err) {
                                  console.error('Failed to copy:', err);
                                }
                              }}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              title="Copy Artifact ID"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Download Button */}
                    {selectedCitation.trace.artifact_id && (
                      <Button
                        className="w-full"
                        onClick={() => handleDownloadArtifact(
                          selectedCitation.trace.artifact_id,
                          selectedCitation.trace.artifact_filename || 'document.pdf'
                        )}
                      >
                        <Download className="h-4 w-4" />
                        Download Source Document
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                    <p className="text-gray-600">Loading evidence trace...</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
      {/* Back to top - fixed bottom right, visible after scrolling down */}
      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="Back to top"
          title="Click to scroll to the top of the page"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}


